import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  fetchTruck,
  updateTruck,
  deleteTruck,
  fetchImpacts,
  createImpact,
  updateImpact,
  deleteImpact,
  fetchComments,
  createComment,
  deleteComment,
  fetchHistory,
  fetchDriverNames,
  createDriver,
  addPhoto,
  deletePhoto,
  fileToBase64,
  uploadPhoto,
  photoUrl,
  api,
  createAppointment,
  fetchAppointments,
  updateAppointment,
  extractVinFromCarteGrise,
  extractDocumentExpiry,
  fetchTruckUsageHistory,
} from "@/lib/api";
import QRCode from "react-qr-code";
import { compressImage } from "@/lib/imageCompress";
import { buildWaLink, buildImpactMessage } from "@/lib/whatsapp";
import { useSites } from "@/lib/sites";
import { exportTruckReport } from "@/lib/pdfExport";
import VanViewer from "@/components/VanViewer";
import TruckLocationCard from "@/components/TruckLocationCard";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { TruckTollBadge } from "@/components/telepeage/TruckTollBadge";
import { toast } from "sonner";
import {
  ArrowLeft,
  Trash,
  Warning,
  WarningOctagon,
  Camera,
  Plus,
  ChatCircle,
  PencilSimple,
  CaretRight,
  CheckCircle,
  Pause,
  Package,
  FilePdf,
  ClockCounterClockwise,
  ArrowRight,
  Wrench,
  Eyeglasses,
  MapPin,
  Tag,
  FileText,
  Calendar,
  WhatsappLogo,
  QrCode,
  UsersThree,
  X,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const SEVERITY = {
  mineur: { label: "Mineur", cls: "sev-mineur" },
  majeur: { label: "Majeur", cls: "sev-majeur" },
  critique: { label: "Critique", cls: "sev-critique" },
};

const STATUS_LABEL = {
  actif: { label: "Actif", cls: "status-actif" },
  inactif: { label: "Inactif", cls: "status-inactif" },
  rendu: { label: "Rendu", cls: "status-rendu" },
  panne: { label: "En panne", cls: "status-panne" },
};

const RENTAL_LABEL = {
  own: "NY TRANS",
  "loc-eco": "Loc Eco",
  europcar: "Europcar",
  hertz: "Hertz",
  ada: "ADA",
  loa: "LOA (Leasing)",
  autre: "Autre",
};

const SITE_LABEL = {
  DLP4: "Perpignan DLP4",
  DWB9: "Rennes DWB9",
};

function ImpactDialog({ open, onClose, position, onSave, editing, drivers = [], onDriverCreated }) {
  const todayStr = () => {
    const d = new Date();
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d - tz).toISOString().slice(0, 10);
  };

  const [form, setForm] = useState({
    severity: "mineur",
    description: "",
    cost: "",
    driver: "",
    date: todayStr(),
    photos: [],
    // Sanction prime
    penalty_enabled: false,
    penalty_months: 1,
    penalty_monthly_amount: "",
    penalty_reason: "",
    penalty_stack: true,
  });

  // Inline "new driver" state
  const [newDriverOpen, setNewDriverOpen] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: "", phone: "", email: "" });
  const [creatingDriver, setCreatingDriver] = useState(false);

  useEffect(() => {
    if (editing) {
      // Back-compat: editing.photos preferred, fallback to single editing.photo
      const initialPhotos = Array.isArray(editing.photos) && editing.photos.length > 0
        ? editing.photos
        : (editing.photo ? [editing.photo] : []);
      setForm({
        severity: editing.severity,
        description: editing.description || "",
        cost: editing.cost || "",
        driver: editing.driver || "",
        date: editing.date ? editing.date.slice(0, 10) : todayStr(),
        photos: initialPhotos,
      });
    } else {
      setForm({
        severity: "mineur",
        description: "",
        cost: "",
        driver: "",
        date: todayStr(),
        photos: [],
        penalty_enabled: false,
        penalty_months: 1,
        penalty_monthly_amount: "",
        penalty_reason: "",
        penalty_stack: true,
      });
    }
  }, [editing, open]);

  const [photoUploadProgress, setPhotoUploadProgress] = useState(null); // { done, total }
  const handlePhoto = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    input.value = "";
    if (files.length === 0) return;
    const MAX_PER_BATCH = 10;
    const batch = files.slice(0, MAX_PER_BATCH);
    if (files.length > MAX_PER_BATCH) {
      toast.message(`${MAX_PER_BATCH} photos max par envoi`);
    }
    setPhotoUploadProgress({ done: 0, total: batch.length });
    const added = [];
    let failed = 0;
    for (let i = 0; i < batch.length; i++) {
      const file = batch[i];
      try {
        if (file.size > 25 * 1024 * 1024) {
          failed += 1;
        } else {
          const compressed = await compressImage(file);
          const { path } = await uploadPhoto(compressed);
          added.push(path);
        }
      } catch {
        failed += 1;
      }
      setPhotoUploadProgress({ done: i + 1, total: batch.length });
      // Yield to the browser so iOS Safari can repaint between heavy ops
      await new Promise((r) => setTimeout(r, 0));
    }
    if (added.length > 0) {
      setForm((f) => ({ ...f, photos: [...(f.photos || []), ...added] }));
    }
    setPhotoUploadProgress(null);
    if (added.length > 0 && failed === 0) {
      toast.success(added.length > 1 ? `${added.length} photos ajoutées` : "Photo ajoutée");
    } else if (added.length > 0 && failed > 0) {
      toast.warning(`${added.length} ajoutée(s), ${failed} échec(s)`);
    } else {
      toast.error("Erreur upload photo");
    }
  };

  const removeFormPhotoAt = (idx) =>
    setForm((f) => ({ ...f, photos: (f.photos || []).filter((_, i) => i !== idx) }));

  const submit = () => {
    const driverName = form.driver.trim();
    const photos = form.photos || [];
    const payload = {
      ...form,
      cost: form.cost ? parseFloat(form.cost) : 0,
      // convert YYYY-MM-DD into a real ISO datetime at noon to avoid TZ issues
      date: form.date ? `${form.date}T12:00:00Z` : undefined,
      driver: driverName,
      photos,
      photo: photos[0] || null,
    };
    // Strip frontend-only penalty fields
    delete payload.penalty_enabled;
    if (form.penalty_enabled && driverName) {
      payload.penalty_months = parseInt(form.penalty_months) || 1;
      payload.penalty_monthly_amount = form.penalty_monthly_amount
        ? parseFloat(form.penalty_monthly_amount)
        : 0;
      payload.penalty_reason = (form.penalty_reason || "").trim();
      payload.penalty_stack = !!form.penalty_stack;
    } else {
      delete payload.penalty_months;
      delete payload.penalty_monthly_amount;
      delete payload.penalty_reason;
      delete payload.penalty_stack;
    }
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none border border-zinc-900 max-w-md w-[calc(100vw-1.5rem)] sm:w-auto max-h-[92vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle
            className="text-xl sm:text-2xl font-black uppercase tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Chivo" }}
          >
            <WarningOctagon size={24} weight="fill" className="text-red-600" />
            {editing ? "Modifier l'impact" : "Nouvel impact"}
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Décrivez le dommage relevé sur le véhicule.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {position && (
            <div className="bg-zinc-100 border border-zinc-300 p-2 text-xs mono">
              Position : x={position.x.toFixed(2)} y={position.y.toFixed(2)} z=
              {position.z.toFixed(2)}
            </div>
          )}
          <div>
            <Label className="label-tag">Gravité</Label>
            <Select
              value={form.severity}
              onValueChange={(v) => setForm({ ...form, severity: v })}
            >
              <SelectTrigger
                className="rounded-none border-zinc-900"
                data-testid="impact-severity"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="mineur">Mineur</SelectItem>
                <SelectItem value="majeur">Majeur</SelectItem>
                <SelectItem value="critique">Critique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tag">Description</Label>
            <Textarea
              className="rounded-none border-zinc-900 min-h-[80px]"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Rayure pare-chocs arrière, choc latéral…"
              data-testid="impact-description"
            />
          </div>
          <div>
            <Label className="label-tag">Coût estimé (€)</Label>
            <Input
              type="number"
              className="rounded-none border-zinc-900 mono"
              value={form.cost}
              onChange={(e) => setForm({ ...form, cost: e.target.value })}
              placeholder="350"
              data-testid="impact-cost"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between">
                <Label className="label-tag">Chauffeur qui a touché</Label>
                <button
                  type="button"
                  onClick={() => {
                    setNewDriver({
                      name: form.driver.trim(),
                      phone: "",
                      email: "",
                    });
                    setNewDriverOpen((v) => !v);
                  }}
                  className="text-[10px] font-bold uppercase tracking-widest text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1"
                  data-testid="impact-driver-add-toggle"
                >
                  <Plus size={10} weight="bold" />
                  {newDriverOpen ? "Fermer" : "Nouveau"}
                </button>
              </div>
              <Input
                list="driver-suggestions"
                className="rounded-none border-zinc-900"
                value={form.driver}
                onChange={(e) => setForm({ ...form, driver: e.target.value })}
                placeholder="Jean Dupont"
                data-testid="impact-driver"
              />
              <datalist id="driver-suggestions">
                {drivers.map((d) => (
                  <option key={d} value={d} />
                ))}
              </datalist>
              {newDriverOpen && (
                <div
                  className="mt-2 border border-zinc-900 bg-zinc-50 p-3 space-y-2"
                  data-testid="impact-new-driver-panel"
                >
                  <div className="label-tag">Créer un chauffeur</div>
                  <Input
                    className="rounded-none border-zinc-900 h-8 text-sm"
                    value={newDriver.name}
                    onChange={(e) =>
                      setNewDriver({ ...newDriver, name: e.target.value })
                    }
                    placeholder="Nom *"
                    data-testid="impact-new-driver-name"
                  />
                  <Input
                    className="rounded-none border-zinc-900 h-8 text-sm mono"
                    value={newDriver.phone}
                    onChange={(e) =>
                      setNewDriver({ ...newDriver, phone: e.target.value })
                    }
                    placeholder="Téléphone (optionnel)"
                    data-testid="impact-new-driver-phone"
                  />
                  <Input
                    type="email"
                    className="rounded-none border-zinc-900 h-8 text-sm"
                    value={newDriver.email}
                    onChange={(e) =>
                      setNewDriver({ ...newDriver, email: e.target.value })
                    }
                    placeholder="Email (optionnel)"
                    data-testid="impact-new-driver-email"
                  />
                  <button
                    type="button"
                    disabled={creatingDriver || !newDriver.name.trim()}
                    onClick={async () => {
                      const name = newDriver.name.trim();
                      if (!name) return;
                      setCreatingDriver(true);
                      try {
                        await createDriver(newDriver);
                        toast.success(`Chauffeur ${name} créé`);
                        setForm((f) => ({ ...f, driver: name }));
                        setNewDriverOpen(false);
                        setNewDriver({ name: "", phone: "", email: "" });
                        if (onDriverCreated) onDriverCreated();
                      } catch (e) {
                        const msg = e?.response?.data?.detail || "Erreur création";
                        // 409 = already exists — still use the name
                        if (e?.response?.status === 409) {
                          setForm((f) => ({ ...f, driver: name }));
                          setNewDriverOpen(false);
                          toast.info("Chauffeur existant sélectionné");
                          if (onDriverCreated) onDriverCreated();
                        } else {
                          toast.error(msg);
                        }
                      } finally {
                        setCreatingDriver(false);
                      }
                    }}
                    className="w-full bg-zinc-900 text-white py-2 text-xs font-bold uppercase tracking-widest disabled:opacity-50"
                    data-testid="impact-new-driver-save"
                  >
                    {creatingDriver ? "Création…" : "Créer & sélectionner"}
                  </button>
                </div>
              )}
            </div>
            <div>
              <Label className="label-tag">Date</Label>
              <Input
                type="date"
                className="rounded-none border-zinc-900 mono"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="impact-date"
              />
            </div>
          </div>
          <div>
            <Label className="label-tag">Photos (optionnelles, plusieurs possibles)</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <label
                className={`flex-1 inline-flex items-center justify-center gap-2 border border-zinc-900 bg-zinc-900 text-white px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-700 transition-none ${photoUploadProgress ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
                data-testid="impact-photo-camera-label"
              >
                <span>{photoUploadProgress ? "Envoi…" : "📷 Prendre une photo"}</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhoto}
                  disabled={!!photoUploadProgress}
                  className="hidden"
                  data-testid="impact-photo-camera-input"
                />
              </label>
              <label
                className={`flex-1 inline-flex items-center justify-center gap-2 border border-zinc-900 bg-white text-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100 transition-none ${photoUploadProgress ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
                data-testid="impact-photo-gallery-label"
              >
                <span>Galerie (multi)</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhoto}
                  disabled={!!photoUploadProgress}
                  className="hidden"
                  data-testid="impact-photo-input"
                />
              </label>
            </div>
            {photoUploadProgress && (
              <div
                className="mt-2 border border-zinc-900 bg-zinc-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest mono flex items-center justify-between"
                data-testid="impact-photo-progress"
              >
                <span>Envoi en cours…</span>
                <span>{photoUploadProgress.done} / {photoUploadProgress.total}</span>
              </div>
            )}
            {(form.photos || []).length === 0 ? (
              <div className="mt-2 min-h-[6rem] border border-dashed border-zinc-300 bg-zinc-50 flex items-center justify-center">
                <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                  {photoUploadProgress ? "Traitement…" : "Aucune photo"}
                </span>
              </div>
            ) : (
              <div className="mt-2 grid grid-cols-3 gap-2" data-testid="impact-photo-grid">
                {(form.photos || []).map((p, idx) => (
                  <div key={`${p}-${idx}`} className="relative border border-zinc-300" data-testid={`impact-photo-item-${idx}`}>
                    <img
                      src={photoUrl(p)}
                      alt={`impact-${idx}`}
                      className="w-full h-20 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeFormPhotoAt(idx)}
                      className="absolute top-1 right-1 bg-red-600 text-white p-1 hover:bg-red-700"
                      aria-label="Supprimer la photo"
                      data-testid={`impact-photo-remove-${idx}`}
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {!editing && (
            <div className="border border-red-300 bg-red-50/40 p-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.penalty_enabled}
                  onChange={(e) => setForm({ ...form, penalty_enabled: e.target.checked })}
                  data-testid="impact-penalty-enable"
                />
                <span className="text-xs font-bold uppercase tracking-widest text-red-800">
                  ⚠ Sanctionner la prime du chauffeur
                </span>
              </label>
              {form.penalty_enabled && (
                <div className="space-y-2 pl-6">
                  <div>
                    <Label className="label-tag">Durée *</Label>
                    <div className="flex gap-1 mt-1">
                      {[1, 2, 3, 6].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setForm({ ...form, penalty_months: n })}
                          className={`flex-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest border ${
                            form.penalty_months === n ? "bg-red-600 border-red-600 text-white" : "bg-white border-zinc-300 hover:border-red-600"
                          }`}
                          data-testid={`impact-penalty-months-${n}`}
                        >
                          {n} mois
                        </button>
                      ))}
                      <Input
                        type="number"
                        min="1"
                        value={form.penalty_months}
                        onChange={(e) => setForm({ ...form, penalty_months: e.target.value })}
                        className="rounded-none border-zinc-300 h-8 w-20 text-sm mono"
                        data-testid="impact-penalty-months"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="label-tag">Montant / mois (€)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.penalty_monthly_amount}
                        onChange={(e) => setForm({ ...form, penalty_monthly_amount: e.target.value })}
                        className="rounded-none border-zinc-300 h-8 mono"
                        placeholder="Ex: 120"
                        data-testid="impact-penalty-amount"
                      />
                    </div>
                    <div>
                      <Label className="label-tag">Motif sanction</Label>
                      <Input
                        value={form.penalty_reason}
                        onChange={(e) => setForm({ ...form, penalty_reason: e.target.value })}
                        className="rounded-none border-zinc-300 h-8 text-sm"
                        placeholder="Ex: Choc latéral"
                        data-testid="impact-penalty-reason"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.penalty_stack}
                      onChange={(e) => setForm({ ...form, penalty_stack: e.target.checked })}
                      data-testid="impact-penalty-stack"
                    />
                    <span className="text-[10px] uppercase tracking-widest text-zinc-600">
                      Cumuler après les sanctions en cours
                    </span>
                  </label>
                  {!form.driver.trim() && (
                    <div className="text-[10px] uppercase tracking-widest text-amber-700">
                      ⚠ Renseigne d&apos;abord le chauffeur pour appliquer la sanction
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-none border-zinc-900"
            data-testid="impact-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            className="rounded-none bg-zinc-900 text-white hover:bg-zinc-700"
            data-testid="impact-save"
          >
            {editing ? "Mettre à jour" : "Enregistrer l'impact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsageHistoryPanel({ usage }) {
  const navigate = useNavigate();
  const stints = usage?.stints || [];

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const [y, m, d] = iso.slice(0, 10).split("-");
      return `${d}/${m}/${y}`;
    } catch {
      return iso;
    }
  };

  const isOngoing = (endDate) => {
    if (!endDate) return false;
    const today = new Date().toISOString().slice(0, 10);
    return endDate >= today;
  };

  if (stints.length === 0) {
    return (
      <div className="text-center text-xs text-zinc-500 py-8" data-testid="usage-empty">
        Aucun historique d&apos;attribution pour ce camion.
      </div>
    );
  }

  return (
    <div data-testid="usage-history">
      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="border border-zinc-900 px-3 py-2 bg-white">
          <div className="label-tag">Chauffeurs distincts</div>
          <div className="text-2xl font-black mono mt-0.5">
            {new Set(stints.map((s) => s.driver_name)).size}
          </div>
        </div>
        <div className="border border-zinc-900 px-3 py-2 bg-white">
          <div className="label-tag">Total jours utilisés</div>
          <div className="text-2xl font-black mono mt-0.5">{usage.total_days || 0}</div>
        </div>
        <div className="border border-zinc-900 px-3 py-2 bg-[var(--ny-gold)] text-zinc-900">
          <div className="label-tag !text-zinc-900 opacity-80">Total kms parcourus</div>
          <div className="text-2xl font-black mono mt-0.5">
            {(usage.total_km || 0).toLocaleString("fr-FR")}
          </div>
        </div>
      </div>

      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 font-bold">
        Historique — du plus récent au plus ancien
      </div>

      <div className="space-y-1">
        {stints.map((s, idx) => {
          const ongoing = isOngoing(s.end_date);
          const singleDay = s.days_count === 1;
          const consecutive = s.consecutive !== false;
          // Build the label describing WHEN the truck was used
          let periodLabel;
          if (singleDay) {
            periodLabel = formatDate(s.start_date);
          } else if (consecutive) {
            periodLabel = `${formatDate(s.start_date)} → ${
              ongoing ? "Aujourd'hui" : formatDate(s.end_date)
            }`;
          } else {
            periodLabel = `entre le ${formatDate(s.start_date)} et le ${formatDate(
              s.end_date
            )}`;
          }
          const datesTooltip = (s.dates || [])
            .map((d) => formatDate(d))
            .join(", ");
          return (
            <div
              key={`${s.driver_name}-${s.start_date}-${idx}`}
              className={`flex flex-wrap items-center gap-3 border px-3 py-2 text-xs ${
                ongoing ? "bg-emerald-50 border-emerald-400" : "bg-white border-zinc-200"
              }`}
              data-testid={`usage-stint-${idx}`}
            >
              {ongoing && (
                <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-emerald-600 text-white">
                  En cours
                </span>
              )}
              <button
                type="button"
                onClick={() => navigate(`/chauffeurs?highlight=${encodeURIComponent(s.driver_name)}`)}
                className="font-bold uppercase tracking-tight text-zinc-900 hover:text-[var(--ny-gold-deep)] hover:underline"
                title="Voir la fiche chauffeur"
                data-testid={`usage-driver-${idx}`}
              >
                {s.driver_name}
              </button>
              {s.site && (
                <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-zinc-900 text-white">
                  {s.site}
                </span>
              )}
              <span
                className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-zinc-900 text-white mono"
                title={
                  singleDay
                    ? "1 seul jour d'utilisation"
                    : consecutive
                    ? `${s.days_count} jours consécutifs`
                    : `${s.days_count} jours dispersés (${datesTooltip})`
                }
              >
                {s.days_count} jour{s.days_count > 1 ? "s" : ""}
              </span>
              <span
                className="text-zinc-500 mono"
                title={!consecutive ? datesTooltip : undefined}
              >
                {periodLabel}
                {!singleDay && !consecutive && (
                  <span className="ml-1 text-amber-600 font-bold" title="Utilisation non continue (jours dispersés)">
                    ⋯
                  </span>
                )}
              </span>
              <div className="ml-auto inline-flex items-center gap-2">
                {s.km_driven > 0 && (
                  <span
                    className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-[var(--ny-gold)] text-zinc-900 mono"
                    title={`Estimé depuis ${s.fuel_entries_count} plein(s) enregistré(s) sur la période`}
                  >
                    {s.km_driven.toLocaleString("fr-FR")} km
                  </span>
                )}
                {s.km_driven === 0 && s.fuel_entries_count <= 1 && (
                  <span
                    className="text-[10px] uppercase tracking-widest text-zinc-400"
                    title="Pas assez de pleins enregistrés pour calculer les kms"
                  >
                    — km
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-[10px] text-zinc-500 italic leading-relaxed">
        <div>
          <b>Jours</b> = nombre exact de journées d&apos;attribution enregistrées.
          <b className="ml-2">« → »</b> = jours consécutifs.
          <b className="ml-2">« entre le … et le … » (⋯)</b> = jours non continus (survolez pour voir les dates).
        </div>
        <div className="mt-1">
          Les <b>kms</b> sont estimés à partir des pleins de carburant enregistrés sur la période
          (km max − km min). Nécessite au minimum 2 pleins pour être calculé.
        </div>
      </div>
    </div>
  );
}


function HistoryTimeline({ history }) {
  const [filter, setFilter] = React.useState("all");

  if (!history || history.length === 0) {
    return (
      <div className="text-center py-10 text-zinc-500">
        <ClockCounterClockwise size={36} weight="duotone" className="mx-auto mb-2" />
        <div className="text-sm font-semibold">Aucun changement</div>
        <div className="text-xs mt-1">
          L&apos;historique enregistre les modifications de statut, Red Tag et expert.
        </div>
      </div>
    );
  }

  const counts = history.reduce(
    (a, h) => {
      a.all++;
      if (h.field === "red_tag") a.red_tag++;
      else if (h.field === "awaiting_expert" || h.field === "expert_visited") a.expert++;
      else if (h.field === "appointment") a.appointment++;
      else if (h.field === "panne") a.panne++;
      else if (h.field === "note") a.note++;
      else a.status++;
      return a;
    },
    { all: 0, red_tag: 0, expert: 0, status: 0, appointment: 0, panne: 0, note: 0 }
  );

  const filtered = history.filter((h) => {
    if (filter === "all") return true;
    if (filter === "red_tag") return h.field === "red_tag";
    if (filter === "expert")
      return h.field === "awaiting_expert" || h.field === "expert_visited";
    if (filter === "status") return h.field === "status";
    if (filter === "appointment") return h.field === "appointment";
    if (filter === "panne") return h.field === "panne";
    if (filter === "note") return h.field === "note";
    return true;
  });

  const fieldMeta = (h) => {
    if (h.field === "appointment") {
      const done = h.to_value === "fait";
      const cancelled = h.to_value === "annule";
      return {
        title: done ? "Maintenance effectuée" : cancelled ? "RDV annulé" : "RDV planifié",
        cls: done
          ? "border-emerald-300 bg-emerald-50"
          : cancelled
          ? "border-zinc-200 bg-zinc-50"
          : "border-sky-300 bg-sky-50",
        accent: done
          ? "bg-emerald-600 text-white"
          : cancelled
          ? "bg-zinc-700 text-white"
          : "bg-sky-600 text-white",
        icon: CheckCircle,
        iconColor: done ? "text-emerald-600" : cancelled ? "text-zinc-500" : "text-sky-600",
      };
    }
    if (h.field === "red_tag") {
      const on = h.to_value === "true" || h.to_value === "True";
      return {
        title: on ? "RED TAG activé" : "RED TAG retiré",
        cls: on ? "border-red-300 bg-red-50" : "border-zinc-200 bg-zinc-50",
        accent: on ? "bg-red-600 text-white" : "bg-zinc-700 text-white",
        icon: WarningOctagon,
        iconColor: on ? "text-red-600" : "text-zinc-500",
      };
    }
    if (h.field === "awaiting_expert") {
      const on = h.to_value === "true" || h.to_value === "True";
      return {
        title: on ? "Mis en attente d'expert" : "Sortie d'attente d'expert",
        cls: on ? "border-purple-300 bg-purple-50" : "border-zinc-200 bg-zinc-50",
        accent: on ? "bg-purple-600 text-white" : "bg-zinc-700 text-white",
        icon: Eyeglasses,
        iconColor: on ? "text-purple-600" : "text-zinc-500",
      };
    }
    if (h.field === "expert_visited") {
      const on = h.to_value === "true" || h.to_value === "True";
      return {
        title: on ? "Expert passé" : "Expert non passé",
        cls: on ? "border-purple-300 bg-purple-50" : "border-zinc-200 bg-zinc-50",
        accent: on ? "bg-purple-700 text-white" : "bg-zinc-700 text-white",
        icon: CheckCircle,
        iconColor: "text-purple-700",
      };
    }
    if (h.field === "note") {
      const kind = h.to_value; // "ajout" | "fait" | "retrait"
      const isDone = kind === "fait";
      const isRemoval = kind === "retrait";
      let title = "Note";
      if (kind === "ajout") title = "Note ajoutée";
      else if (isDone) title = "Note validée";
      else if (isRemoval) title = "Note retirée";
      return {
        title,
        cls: isDone
          ? "border-emerald-300 bg-emerald-50"
          : isRemoval
          ? "border-zinc-200 bg-zinc-50"
          : "border-amber-200 bg-amber-50",
        accent: isDone
          ? "bg-emerald-600 text-white"
          : isRemoval
          ? "bg-zinc-700 text-white"
          : "bg-amber-500 text-white",
        icon: isDone ? CheckCircle : Wrench,
        iconColor: isDone ? "text-emerald-600" : "text-amber-600",
      };
    }
    if (h.field === "panne") {
      const kind = h.to_value; // "ajout" | "retrait" | "réparée" | severity
      const isRepair = kind === "réparée";
      const isRemoval = kind === "retrait";
      let title = "Panne modifiée";
      if (kind === "ajout") title = "Panne ajoutée";
      else if (isRemoval) title = "Panne retirée";
      else if (isRepair) title = "Panne réparée";
      else if (kind === "majeur" || kind === "mineur") title = `Sévérité → ${kind}`;
      return {
        title,
        cls: isRepair
          ? "border-emerald-300 bg-emerald-50"
          : isRemoval
          ? "border-zinc-200 bg-zinc-50"
          : "border-amber-300 bg-amber-50",
        accent: isRepair
          ? "bg-emerald-600 text-white"
          : isRemoval
          ? "bg-zinc-700 text-white"
          : "bg-amber-600 text-white",
        icon: isRepair ? CheckCircle : Wrench,
        iconColor: isRepair ? "text-emerald-600" : "text-amber-600",
      };
    }
    // status
    const v = h.to_value;
    const map = {
      actif: { cls: "border-green-300 bg-green-50", accent: "bg-green-600 text-white" },
      inactif: { cls: "border-zinc-300 bg-zinc-50", accent: "bg-zinc-600 text-white" },
      rendu: { cls: "border-blue-300 bg-blue-50", accent: "bg-blue-600 text-white" },
      panne: { cls: "border-amber-300 bg-amber-50", accent: "bg-amber-600 text-white" },
    };
    const m = map[v] || { cls: "border-zinc-200 bg-zinc-50", accent: "bg-zinc-900 text-white" };
    return {
      title: `Statut → ${v}`,
      cls: m.cls,
      accent: m.accent,
      icon: v === "panne" ? Wrench : Package,
      iconColor: v === "panne" ? "text-amber-600" : "text-zinc-700",
    };
  };

  return (
    <div>
      <div className="flex gap-1 flex-wrap mb-3" data-testid="history-filter-bar">
        {[
          { v: "all", l: `Tous · ${counts.all}` },
          { v: "red_tag", l: `Red Tag · ${counts.red_tag}` },
          { v: "expert", l: `Expert · ${counts.expert}` },
          { v: "status", l: `Statut · ${counts.status}` },
          { v: "panne", l: `Pannes · ${counts.panne}` },
          { v: "note", l: `Notes · ${counts.note}` },
          { v: "appointment", l: `Maintenance · ${counts.appointment}` },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest border transition-none ${
              filter === f.v
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
            }`}
            data-testid={`history-filter-${f.v}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-6 text-zinc-500 text-sm">Aucune entrée dans cette catégorie.</div>
        )}
        {filtered.map((h) => {
          const meta = fieldMeta(h);
          const Icon = meta.icon;
          return (
            <div
              key={h.id}
              className={`border p-3 ${meta.cls}`}
              data-testid={`history-entry-${h.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon size={16} weight="fill" className={meta.iconColor} />
                  <span className="font-bold text-sm tracking-tight truncate" style={{ fontFamily: "Chivo" }}>
                    {meta.title}
                  </span>
                </div>
                <span className="text-[11px] text-zinc-500 mono shrink-0">
                  {new Date(h.date).toLocaleString("fr-FR")}
                </span>
              </div>
              {h.note && (
                <div className="mt-2 text-sm text-zinc-700 whitespace-pre-wrap leading-snug">
                  {h.note}
                </div>
              )}
              <div className="flex items-center gap-2 mt-2 text-xs">
                <span className="px-2 py-0.5 bg-zinc-200 text-zinc-700 font-mono">
                  {h.from_value || "—"}
                </span>
                <ArrowRight size={12} weight="bold" />
                <span className={`px-2 py-0.5 font-mono ${meta.accent}`}>
                  {h.to_value || "—"}
                </span>
                <span className="ml-auto text-zinc-500">par {h.author}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ImpactCard({ impact, isActive, onSelect, onEdit, onDelete, onPhotoOpen, driverPhone, truckPlate }) {
  const sev = SEVERITY[impact.severity];
  const waHref = (impact.driver && driverPhone)
    ? buildWaLink(driverPhone, buildImpactMessage({
        driverName: impact.driver,
        severity: sev.label,
        description: impact.description,
        plate: truckPlate,
      }))
    : null;
  return (
    <div
      className={`bg-white border p-3 cursor-pointer transition-none ${
        isActive ? "border-zinc-900 brutalist-shadow-sm" : "border-zinc-200"
      }`}
      onClick={onSelect}
      data-testid={`impact-card-${impact.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${sev.cls}`}>
            {sev.label}
          </span>
          <span className="text-xs text-zinc-500 mono">
            {new Date(impact.date).toLocaleDateString("fr-FR")}
          </span>
        </div>
        <div className="flex gap-1">
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-1 hover:bg-emerald-100 text-emerald-700 inline-flex items-center"
              title={`Prévenir ${impact.driver} sur WhatsApp`}
              data-testid={`whatsapp-impact-${impact.id}`}
            >
              <WhatsappLogo size={14} weight="bold" />
            </a>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="p-1 hover:bg-zinc-100"
            data-testid={`edit-impact-${impact.id}`}
          >
            <PencilSimple size={14} weight="bold" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1 hover:bg-red-100"
            data-testid={`delete-impact-${impact.id}`}
          >
            <Trash size={14} weight="bold" className="text-red-600" />
          </button>
        </div>
      </div>
      {impact.description && (
        <div className="text-sm mt-2 leading-snug">{impact.description}</div>
      )}
      {impact.driver ? (
        <div className="text-xs mt-1.5 inline-flex items-center gap-1 bg-zinc-900 text-white px-2 py-0.5 font-bold uppercase tracking-widest">
          chauffeur · {impact.driver}
        </div>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="text-xs mt-1.5 inline-flex items-center gap-1 bg-amber-100 border border-amber-500 text-amber-800 hover:bg-amber-200 px-2 py-0.5 font-bold uppercase tracking-widest"
          title="Attribuer un chauffeur à cet impact"
          data-testid={`reattribuer-impact-${impact.id}`}
        >
          <PencilSimple size={11} weight="bold" /> Réattribuer un chauffeur
        </button>
      )}
      <div className="flex items-center justify-between mt-2">
        <div className="text-xs text-zinc-500 mono">
          x{impact.position.x.toFixed(1)} y{impact.position.y.toFixed(1)} z
          {impact.position.z.toFixed(1)}
        </div>
        {impact.cost > 0 && (
          <div className="text-sm font-bold mono">{impact.cost.toLocaleString()} €</div>
        )}
      </div>
      {(() => {
        const photos = (Array.isArray(impact.photos) && impact.photos.length > 0)
          ? impact.photos
          : (impact.photo ? [impact.photo] : []);
        if (photos.length === 0) return null;
        if (photos.length === 1) {
          return (
            <img
              src={photoUrl(photos[0])}
              alt="impact"
              className="mt-2 w-full h-24 object-cover border border-zinc-300 cursor-zoom-in"
              onClick={(e) => {
                e.stopPropagation();
                onPhotoOpen && onPhotoOpen(0);
              }}
              data-testid={`impact-photo-${impact.id}`}
            />
          );
        }
        return (
          <div className="mt-2 grid grid-cols-3 gap-1" data-testid={`impact-photos-${impact.id}`}>
            {photos.map((p, idx) => (
              <img
                key={`${p}-${idx}`}
                src={photoUrl(p)}
                alt={`impact-${idx}`}
                className="w-full h-16 object-cover border border-zinc-300 cursor-zoom-in"
                onClick={(e) => {
                  e.stopPropagation();
                  onPhotoOpen && onPhotoOpen(idx);
                }}
                data-testid={`impact-photo-${impact.id}-${idx}`}
              />
            ))}
          </div>
        );
      })()}
    </div>
  );
}


function DocumentsPanel({ truck, onSaved }) {
  const [docs, setDocs] = useState({
    insurance_expiry: truck.insurance_expiry ? truck.insurance_expiry.slice(0, 10) : "",
    insurance_doc: truck.insurance_doc || "",
    insurance_closed: !!truck.insurance_closed,
    insurance_closed_at: truck.insurance_closed_at ? truck.insurance_closed_at.slice(0, 10) : "",
    technical_check_expiry: truck.technical_check_expiry ? truck.technical_check_expiry.slice(0, 10) : "",
    technical_check_doc: truck.technical_check_doc || "",
    transport_license_expiry: truck.transport_license_expiry ? truck.transport_license_expiry.slice(0, 10) : "",
    transport_license_doc: truck.transport_license_doc || "",
    pollution_control_expiry: truck.pollution_control_expiry ? truck.pollution_control_expiry.slice(0, 10) : "",
    pollution_control_doc: truck.pollution_control_doc || "",
    carte_grise_doc: truck.carte_grise_doc || "",
  });
  const [uploading, setUploading] = useState(null);
  const [vin, setVin] = useState(truck.vin || "");
  const [extractingVin, setExtractingVin] = useState(false);
  const [extractingExpiry, setExtractingExpiry] = useState(null); // doc_type key being extracted
  const [plateWarnings, setPlateWarnings] = useState({}); // { technical_check: {detected, on_truck}, pollution_control: {...} }

  const daysUntil = (d) => {
    if (!d) return null;
    const target = new Date(d + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.floor((target - today) / (1000 * 60 * 60 * 24));
  };

  const StatusBadge = ({ days }) => {
    if (days === null || days === undefined) {
      return <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-zinc-100 text-zinc-500 border border-zinc-300">Non renseigné</span>;
    }
    if (days < 0) return <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white">Expiré · {Math.abs(days)}j</span>;
    if (days <= 7) return <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-orange-600 text-white">Urgent · {days}j</span>;
    if (days <= 30) return <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-amber-400 text-zinc-900">Bientôt · {days}j</span>;
    return <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-emerald-500 text-white">OK · {days}j</span>;
  };

  const uploadFor = async (field, file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("Fichier trop volumineux (max 25 MB)");
    setUploading(field);
    try {
      const toSend = file.type && file.type.startsWith("image/") ? await compressImage(file) : file;
      const { path } = await uploadPhoto(toSend);
      const next = { ...docs, [field]: path };
      setDocs(next);
      await updateTruck(truck.id, { [field]: path });
      toast.success("Document enregistré");
      onSaved && onSaved();
      // Auto-extract VIN when the carte grise is (re)uploaded
      if (field === "carte_grise_doc" && path) {
        setExtractingVin(true);
        try {
          const res = await extractVinFromCarteGrise(truck.id);
          if (res.detected && res.vin) {
            setVin(res.vin);
            if (res.valid_length === false) {
              toast.warning(`VIN extrait (${res.vin.length} caractères — vérifie).`);
            } else {
              toast.success(`VIN extrait : ${res.vin}`);
            }
            onSaved && onSaved();
          } else if (res.detected && res.first_registration_date) {
            toast.message("Date d'immatriculation détectée — VIN introuvable");
          } else {
            toast.message("VIN introuvable — saisis-le manuellement");
          }
          // Show a dedicated notification when the first CT was auto-planned
          if (res.first_technical_check_auto?.planned_date) {
            const info = res.first_technical_check_auto;
            if (info.auto_applied) {
              setDocs((p) => ({ ...p, technical_check_expiry: info.planned_date }));
              toast.success(
                `Premier CT programmé automatiquement au ${info.planned_date} · véhicule neuf (règle 4 ans)`
              );
            } else {
              toast.message(
                `Info : le premier CT réglementaire tomberait le ${info.planned_date} (véhicule < 4 ans). Date existante conservée.`
              );
            }
            onSaved && onSaved();
          }
          // Same for the first antipollution check (aligné sur le 1er CT)
          if (res.first_pollution_check_auto?.planned_date) {
            const info = res.first_pollution_check_auto;
            if (info.auto_applied) {
              setDocs((p) => ({ ...p, pollution_control_expiry: info.planned_date }));
              toast.success(
                `Premier contrôle antipollution programmé au ${info.planned_date}`
              );
            }
            onSaved && onSaved();
          }
        } catch (e) {
          const msg = e?.response?.data?.detail || "Extraction du VIN échouée";
          toast.message(msg);
        } finally {
          setExtractingVin(false);
        }
      }
      // Auto-extract expiry date for technical control / antipollution
      const AUTO_EXPIRY_MAP = {
        technical_check_doc: { type: "technical_check", expField: "technical_check_expiry", years: 2 },
        pollution_control_doc: { type: "pollution_control", expField: "pollution_control_expiry", years: 1 },
      };
      const autoCfg = AUTO_EXPIRY_MAP[field];
      if (autoCfg && path) {
        setExtractingExpiry(autoCfg.type);
        try {
          const res = await extractDocumentExpiry(truck.id, autoCfg.type);
          // Persist plate check result for this doc type
          setPlateWarnings((p) => ({
            ...p,
            [autoCfg.type]: {
              matches: res.plate_matches,
              detected: res.license_plate_detected,
              on_truck: res.license_plate_on_truck,
            },
          }));
          if (res.detected && res.expiry_date) {
            setDocs((p) => ({ ...p, [autoCfg.expField]: res.expiry_date }));
            const suffix = res.computed_from_issue
              ? ` (calculée : ${res.issue_date} + ${autoCfg.years} an${
                  autoCfg.years > 1 ? "s" : ""
                })`
              : "";
            toast.success(`Date d'expiration détectée : ${res.expiry_date}${suffix}`);
            onSaved && onSaved();
          } else {
            toast.message("Date introuvable — saisis-la manuellement");
          }
          if (res.plate_matches === false) {
            toast.warning(
              `Plaque du document (${res.license_plate_detected}) ≠ plaque du camion (${res.license_plate_on_truck}) — vérifie le fichier`
            );
          }
        } catch (e) {
          toast.message(e?.response?.data?.detail || "Analyse du document échouée");
        } finally {
          setExtractingExpiry(null);
        }
      }
    } catch { toast.error("Erreur d'upload"); }
    finally { setUploading(null); }
  };

  const saveVin = async () => {
    const clean = (vin || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    setVin(clean);
    try {
      await updateTruck(truck.id, { vin: clean });
      toast.success("VIN enregistré");
      onSaved && onSaved();
    } catch {
      toast.error("Erreur");
    }
  };

  const runExtract = async () => {
    if (!docs.carte_grise_doc) {
      toast.error("Ajoute d'abord la carte grise");
      return;
    }
    setExtractingVin(true);
    try {
      const res = await extractVinFromCarteGrise(truck.id);
      if (res.detected && res.vin) {
        setVin(res.vin);
        toast.success(`VIN extrait : ${res.vin}`);
      } else {
        toast.message("VIN introuvable — saisis-le manuellement");
      }
      if (res.first_technical_check_auto?.planned_date) {
        const info = res.first_technical_check_auto;
        if (info.auto_applied) {
          setDocs((p) => ({ ...p, technical_check_expiry: info.planned_date }));
          toast.success(
            `Premier CT programmé automatiquement au ${info.planned_date} · véhicule neuf (règle 4 ans)`
          );
        } else {
          toast.message(
            `Info : le premier CT réglementaire tomberait le ${info.planned_date} (véhicule < 4 ans). Date existante conservée.`
          );
        }
      }
      if (res.first_pollution_check_auto?.planned_date) {
        const info = res.first_pollution_check_auto;
        if (info.auto_applied) {
          setDocs((p) => ({ ...p, pollution_control_expiry: info.planned_date }));
          toast.success(
            `Premier contrôle antipollution programmé au ${info.planned_date}`
          );
        }
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction échouée");
    } finally {
      setExtractingVin(false);
    }
  };

  const saveExpiry = async (field, value) => {
    const next = { ...docs, [field]: value };
    setDocs(next);
    try {
      const body = {};
      body[field] = value ? new Date(value + "T12:00:00").toISOString() : null;
      await updateTruck(truck.id, body);
      toast.success("Date enregistrée");
      onSaved && onSaved();
    } catch { toast.error("Erreur"); }
  };

  const saveInsuranceClosure = async (closed) => {
    const nowIso = new Date().toISOString();
    const closedAt = closed ? nowIso.slice(0, 10) : "";
    setDocs((p) => ({ ...p, insurance_closed: closed, insurance_closed_at: closedAt }));
    try {
      await updateTruck(truck.id, {
        insurance_closed: closed,
        insurance_closed_at: closed ? nowIso : null,
      });
      toast.success(closed ? "Assurance marquée clôturée" : "Assurance réactivée");
      onSaved && onSaved();
    } catch { toast.error("Erreur"); }
  };

  const removeDoc = async (field) => {
    setDocs((p) => ({ ...p, [field]: "" }));
    try {
      await updateTruck(truck.id, { [field]: null });
      onSaved && onSaved();
      toast.success("Document retiré");
    } catch { toast.error("Erreur"); }
  };

  const sections = [
    // No expiry: insurance auto-renewed; we only track presence + closure state
    { key: "insurance", title: "Assurance", expField: null, docField: "insurance_doc", supportsClosure: true },
    { key: "tc", title: "Contrôle technique", expField: "technical_check_expiry", docField: "technical_check_doc", ocrType: "technical_check", ocrYears: 2 },
    { key: "license", title: "Licence de transport", expField: "transport_license_expiry", docField: "transport_license_doc" },
    { key: "pollution", title: "Contrôle pollution", expField: "pollution_control_expiry", docField: "pollution_control_doc", ocrType: "pollution_control", ocrYears: 1 },
    // No expiry: French vehicle registration ("carte grise") only needs presence check
    { key: "carte_grise", title: "Carte grise", expField: null, docField: "carte_grise_doc" },
  ];

  const runExtractExpiry = async (section) => {
    if (!section.ocrType) return;
    if (!docs[section.docField]) {
      toast.error("Ajoute d'abord un document à analyser");
      return;
    }
    setExtractingExpiry(section.ocrType);
    try {
      const res = await extractDocumentExpiry(truck.id, section.ocrType);
      setPlateWarnings((p) => ({
        ...p,
        [section.ocrType]: {
          matches: res.plate_matches,
          detected: res.license_plate_detected,
          on_truck: res.license_plate_on_truck,
        },
      }));
      if (res.detected && res.expiry_date) {
        setDocs((p) => ({ ...p, [section.expField]: res.expiry_date }));
        const suffix = res.computed_from_issue
          ? ` (calculée : ${res.issue_date} + ${res.renewal_years} an${
              res.renewal_years > 1 ? "s" : ""
            })`
          : "";
        toast.success(`Date d'expiration : ${res.expiry_date}${suffix}`);
        onSaved && onSaved();
      } else {
        toast.message(
          "Aucune date détectée — saisis-la manuellement ou téléverse une image plus lisible"
        );
      }
      if (res.plate_matches === false) {
        toast.warning(
          `Plaque du document (${res.license_plate_detected}) ≠ plaque du camion (${res.license_plate_on_truck}) — vérifie le fichier`
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse du document échouée");
    } finally {
      setExtractingExpiry(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="documents-panel-inner">
      {/* ---- VIN + QR Code card ---- */}
      <div className="border border-zinc-900 p-3 flex flex-col sm:flex-row items-start gap-4 bg-zinc-50" data-testid="vin-qr-card">
        <div className="flex-1 w-full">
          <div className="label-tag mb-1">Numéro VIN (section E carte grise)</div>
          <div className="flex flex-col sm:flex-row gap-2 items-stretch">
            <Input
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase())}
              onBlur={saveVin}
              className="rounded-none border-zinc-900 mono font-bold tracking-widest flex-1"
              placeholder="VF3VFEHTMM7862773"
              maxLength={17}
              data-testid="input-vin"
            />
            <button
              onClick={runExtract}
              disabled={extractingVin || !docs.carte_grise_doc}
              className="inline-flex items-center justify-center gap-2 border border-zinc-900 bg-zinc-900 text-white px-3 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-700 disabled:opacity-40 whitespace-nowrap"
              data-testid="btn-extract-vin"
              title="Ré-extraire depuis la carte grise (Gemini vision)"
            >
              {extractingVin ? "Extraction…" : "🔍 Extraire depuis carte grise"}
            </button>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
            {vin && vin.length !== 17 && (
              <span className="text-amber-700">⚠ {vin.length}/17 caractères — vérifie</span>
            )}
            {!vin && !docs.carte_grise_doc && "Attache la carte grise pour extraire automatiquement le VIN"}
            {!vin && docs.carte_grise_doc && "Clique sur 'Extraire' ou renseigne manuellement le VIN"}
            {vin && vin.length === 17 && "VIN valide · le QR code est prêt à être scanné"}
          </div>
        </div>
        <div className="w-32 h-32 shrink-0 flex items-center justify-center border border-zinc-300 bg-white p-2" data-testid="vin-qr-code">
          {vin ? (
            <QRCode
              value={vin}
              size={112}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              viewBox="0 0 256 256"
            />
          ) : (
            <span className="text-[9px] uppercase tracking-widest text-zinc-400 text-center font-bold">
              Aucun VIN — pas de QR
            </span>
          )}
        </div>
      </div>
      {sections.map((s) => {
        const noExpiry = s.expField === null;
        const expiryValue = noExpiry ? "" : docs[s.expField];
        const days = noExpiry ? null : daysUntil(expiryValue);
        const docValue = docs[s.docField];
        const isMissing = noExpiry && !docValue;
        const isInsurance = s.key === "insurance";
        const insClosed = !!docs.insurance_closed;
        return (
          <div key={s.key} className="border border-zinc-300 p-3" data-testid={`doc-${s.key}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="font-bold uppercase tracking-tight text-sm">{s.title}</div>
              {isInsurance ? (
                <span
                  className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border ${
                    insClosed
                      ? "border-zinc-700 bg-zinc-100 text-zinc-700"
                      : docValue
                        ? "border-emerald-700 bg-emerald-50 text-emerald-800"
                        : "border-red-700 bg-red-50 text-red-800"
                  }`}
                  data-testid={`doc-${s.key}-status`}
                >
                  {insClosed ? "Clôturée" : docValue ? "Active" : "Aucun document"}
                </span>
              ) : noExpiry ? (
                <span
                  className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border ${
                    docValue
                      ? "border-emerald-700 bg-emerald-50 text-emerald-800"
                      : "border-red-700 bg-red-50 text-red-800"
                  }`}
                  data-testid={`doc-${s.key}-status`}
                >
                  {docValue ? "Présent" : "Manquant"}
                </span>
              ) : (
                <StatusBadge days={days} />
              )}
            </div>
            {s.ocrType && plateWarnings[s.ocrType]?.matches === false && (
              <div
                className="mb-3 border-2 border-red-600 bg-red-50 p-2 flex items-start gap-2"
                data-testid={`doc-${s.key}-plate-warning`}
              >
                <Warning size={18} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
                <div className="flex-1 text-xs">
                  <div className="font-bold uppercase tracking-widest text-red-800">
                    Attention — plaque non correspondante
                  </div>
                  <div className="text-red-700 mt-0.5">
                    Le document mentionne la plaque{" "}
                    <span className="mono font-bold">
                      {plateWarnings[s.ocrType].detected || "?"}
                    </span>
                    , mais ce camion est immatriculé{" "}
                    <span className="mono font-bold">
                      {plateWarnings[s.ocrType].on_truck || "?"}
                    </span>
                    . Vérifie que tu attaches le bon fichier.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setPlateWarnings((p) => {
                      const next = { ...p };
                      delete next[s.ocrType];
                      return next;
                    })
                  }
                  className="text-red-700 hover:text-red-900 shrink-0"
                  data-testid={`doc-${s.key}-plate-warning-dismiss`}
                  title="Ignorer l'avertissement"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            )}
            {isInsurance && (
              <div className="mb-3 border border-zinc-200 bg-zinc-50 p-2">
                <label
                  className="flex items-center gap-3 cursor-pointer"
                  data-testid="insurance-closure-toggle"
                >
                  <input
                    type="checkbox"
                    checked={insClosed}
                    onChange={(e) => saveInsuranceClosure(e.target.checked)}
                    className="w-4 h-4 accent-zinc-900"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-bold">Assurance clôturée</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                      Coche cette case quand le camion est rendu et l&apos;assurance résiliée
                    </div>
                  </div>
                  {insClosed && docs.insurance_closed_at && (
                    <span className="text-[10px] mono text-zinc-500">
                      Depuis le {docs.insurance_closed_at}
                    </span>
                  )}
                </label>
              </div>
            )}
            {isInsurance || noExpiry ? (
              <div>
                <Label className="label-tag">Document</Label>
                <label
                  className="block bg-zinc-900 text-white text-center py-1.5 px-2 text-[10px] uppercase tracking-widest font-bold cursor-pointer hover:bg-zinc-700"
                  data-testid={`doc-${s.key}-upload-label`}
                >
                  {uploading === s.docField ? "Upload..." : (docValue ? "Remplacer le fichier" : "Téléverser PDF / photo")}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => uploadFor(s.docField, e.target.files?.[0])}
                    className="hidden"
                    data-testid={`doc-${s.key}-upload-input`}
                  />
                </label>
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                  {isInsurance
                    ? "Renouvellement automatique — pas de date d'expiration à saisir."
                    : "Pas de date d'expiration — présence uniquement."}
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <Label className="label-tag">Date d&apos;expiration *</Label>
                  <Input
                    key={`${s.key}-${truck.id}-${expiryValue}`}
                    type="date"
                    defaultValue={expiryValue}
                    onBlur={(e) => {
                      if (e.target.value !== expiryValue) {
                        saveExpiry(s.expField, e.target.value);
                      }
                    }}
                    className="rounded-none border-zinc-900"
                    data-testid={`doc-${s.key}-date`}
                  />
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                    La date est enregistrée quand tu cliques ailleurs
                  </div>
                </div>
                <div>
                  <Label className="label-tag">Document (optionnel)</Label>
                  <label className="block bg-zinc-900 text-white text-center py-1.5 px-2 text-[10px] uppercase tracking-widest font-bold cursor-pointer hover:bg-zinc-700" data-testid={`doc-${s.key}-upload-label`}>
                    {uploading === s.docField ? "Upload..." : (docValue ? "Remplacer le fichier" : "Téléverser PDF / photo")}
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => uploadFor(s.docField, e.target.files?.[0])}
                      className="hidden"
                      data-testid={`doc-${s.key}-upload-input`}
                    />
                  </label>
                  {s.ocrType && (
                    <button
                      type="button"
                      onClick={() => runExtractExpiry(s)}
                      disabled={!docValue || extractingExpiry === s.ocrType}
                      className="w-full mt-1 inline-flex items-center justify-center gap-2 border border-zinc-900 bg-white text-zinc-900 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-zinc-900"
                      data-testid={`doc-${s.key}-extract-expiry`}
                      title={
                        docValue
                          ? `Analyser le document et détecter la date d'expiration (renouvellement ${s.ocrYears} an${s.ocrYears > 1 ? "s" : ""})`
                          : "Téléverse d'abord le document"
                      }
                    >
                      {extractingExpiry === s.ocrType
                        ? "Analyse en cours…"
                        : "Analyser · Détecter la date"}
                    </button>
                  )}
                </div>
              </div>
            )}
            {docValue && (
              <div className="mt-2 inline-flex items-center gap-2 border border-zinc-200 px-2 py-1">
                <FileText size={14} weight="bold" />
                <a
                  href={photoUrl(docValue)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-bold uppercase tracking-widest underline"
                  data-testid={`doc-${s.key}-view`}
                >
                  Voir le document
                </a>
                <button
                  onClick={() => removeDoc(s.docField)}
                  className="text-xs font-bold uppercase text-red-600 ml-2"
                  data-testid={`doc-${s.key}-remove`}
                >
                  Retirer
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function UpcomingAppointmentsCard({ appointments, onValidate, onCancel, onNew, onSeeCalendar }) {
  const TYPE_LABEL = {
    expert: "Passage expert",
    clim: "Recharge clim",
    controle_technique: "Contrôle technique",
    revision: "Révision",
    mecanique: "Mécanique",
    carrosserie: "Carrosserie",
    autre: "RDV",
  };
  const TYPE_COLOR = {
    expert: "#7C3AED",
    clim: "#0EA5E9",
    controle_technique: "#F59E0B",
    revision: "#10B981",
    mecanique: "#EF4444",
    carrosserie: "#EC4899",
    autre: "#52525B",
  };

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  // Only "planifie" (not yet validated/cancelled). Includes past ones (missed).
  const all = (appointments || [])
    .filter((a) => a.status === "planifie")
    .map((a) => ({ ...a, _d: new Date(a.date) }))
    .filter((a) => !isNaN(a._d))
    .sort((a, b) => a._d - b._d);

  const missed = all.filter((a) => a._d < startOfToday);
  const upcoming = all.filter((a) => a._d >= startOfToday);

  const today = new Date();
  const sameDay = (d) =>
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();

  const fmtDate = (d) =>
    d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  const fmtTime = (d) =>
    `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const daysAgo = (d) => Math.floor((startOfToday - d) / (1000 * 60 * 60 * 24));

  const ActionButtons = ({ a, missed: isMissed }) => (
    <div className="flex flex-wrap gap-1">
      <button
        onClick={() => onValidate(a)}
        className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-emerald-600 text-white hover:bg-emerald-700"
        data-testid={`upcoming-rdv-validate-${a.id}`}
      >
        ✓ Valider
      </button>
      <button
        onClick={() => onCancel(a)}
        className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-red-600 hover:text-red-600"
        data-testid={`upcoming-rdv-cancel-${a.id}`}
      >
        ✕ Annuler
      </button>
    </div>
  );

  const total = missed.length + upcoming.length;

  return (
    <div className="bg-white border border-black mb-3" data-testid="upcoming-rdv-card">
      <div className="flex items-center justify-between px-3 py-2 border-b border-black bg-black text-white">
        <div className="label-tag !text-[var(--ny-gold-soft)]">RDV à venir</div>
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">
          {total === 0
            ? "0"
            : `${total} ${missed.length > 0 ? `(${missed.length} loupé${missed.length > 1 ? "s" : ""})` : ""}`}
        </div>
      </div>

      {total === 0 ? (
        <div className="px-3 py-4 text-center">
          <div className="text-sm text-zinc-500 mb-2">
            Aucun RDV planifié pour ce camion.
          </div>
          <button
            onClick={onNew}
            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-black bg-white hover:bg-black hover:text-white"
            data-testid="upcoming-rdv-new"
          >
            + Planifier un RDV
          </button>
        </div>
      ) : (
        <>
          {/* Missed appointments — red banner */}
          {missed.length > 0 && (
            <div className="bg-red-50 border-b-2 border-red-600">
              <div className="px-3 py-1.5 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest flex items-center justify-between">
                <span>⚠ {missed.length} RDV loupé{missed.length > 1 ? "s" : ""}</span>
                <span className="opacity-80">Effectué ou pas ?</span>
              </div>
              <div className="divide-y divide-red-200">
                {missed.slice(0, 4).map((a) => (
                  <div
                    key={a.id}
                    className="px-3 py-2 flex flex-wrap items-center justify-between gap-2"
                    data-testid={`missed-rdv-${a.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="px-1.5 py-0.5 text-[10px] font-bold uppercase text-white whitespace-nowrap"
                        style={{ background: TYPE_COLOR[a.type] || "#52525B" }}
                      >
                        {TYPE_LABEL[a.type] || a.type}
                      </span>
                      <div className="min-w-0">
                        <div className="text-xs font-bold mono uppercase">
                          {fmtDate(a._d)} · {fmtTime(a._d)}
                          <span className="ml-1 text-[10px] text-red-700 font-bold">
                            · IL Y A {daysAgo(a._d)}J
                          </span>
                        </div>
                        {(a.title || a.location) && (
                          <div className="text-[11px] text-zinc-600 truncate">
                            {a.title}
                            {a.location ? ` · ${a.location}` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                    <ActionButtons a={a} missed />
                  </div>
                ))}
                {missed.length > 4 && (
                  <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-red-700">
                    + {missed.length - 4} autre(s) loupé(s)
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Upcoming */}
          <div className="divide-y divide-zinc-200">
            {upcoming.slice(0, 4).map((a) => {
              const today_ = sameDay(a._d);
              return (
                <div
                  key={a.id}
                  className={`px-3 py-2 flex flex-wrap items-center justify-between gap-2 ${
                    today_ ? "bg-amber-50" : ""
                  }`}
                  data-testid={`upcoming-rdv-${a.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="px-1.5 py-0.5 text-[10px] font-bold uppercase text-white whitespace-nowrap"
                      style={{ background: TYPE_COLOR[a.type] || "#52525B" }}
                    >
                      {TYPE_LABEL[a.type] || a.type}
                    </span>
                    <div className="min-w-0">
                      <div className="text-xs font-bold mono uppercase">
                        {fmtDate(a._d)} · {fmtTime(a._d)}
                        {today_ && (
                          <span className="ml-1 text-[10px] text-amber-700 font-bold">
                            · AUJOURD&apos;HUI
                          </span>
                        )}
                      </div>
                      {(a.title || a.location) && (
                        <div className="text-[11px] text-zinc-600 truncate">
                          {a.title}
                          {a.location ? ` · ${a.location}` : ""}
                        </div>
                      )}
                    </div>
                  </div>
                  <ActionButtons a={a} />
                </div>
              );
            })}
          </div>
          {upcoming.length > 4 && (
            <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-t border-zinc-200">
              + {upcoming.length - 4} autre(s) à venir
            </div>
          )}
          <div className="px-3 py-2 flex flex-wrap items-center justify-end gap-2 border-t border-zinc-200 bg-zinc-50">
            <button
              onClick={onNew}
              className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-black"
            >
              + Nouveau RDV
            </button>
            <button
              onClick={onSeeCalendar}
              className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-black"
            >
              Voir calendrier →
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function EditTruckInfoDialog({ truck, owners, onSaved }) {
  const { sites: sitesList } = useSites();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    license_plate: "",
    brand: "",
    model: "",
    year: "",
    mileage: "",
    van_type: "trafic",
    site: "DLP4",
    rental_type: "own",
    rental_type_custom: "",
    monthly_rent: "",
    daily_revenue: "200",
    rental_agency: "",
  });

  useEffect(() => {
    if (open && truck) {
      setForm({
        license_plate: truck.license_plate || "",
        brand: truck.brand || "",
        model: truck.model || "",
        year: truck.year ? String(truck.year) : "",
        mileage: truck.mileage ? String(truck.mileage) : "",
        van_type: truck.van_type || "trafic",
        site: truck.site || "DLP4",
        rental_type: truck.rental_type || "own",
        rental_type_custom: truck.rental_type_custom || "",
        monthly_rent: truck.monthly_rent != null ? String(truck.monthly_rent) : "",
        daily_revenue: truck.daily_revenue != null ? String(truck.daily_revenue) : "200",
        rental_agency: truck.rental_agency || "",
      });
    }
  }, [open, truck]);

  const submit = async () => {
    if (!form.license_plate.trim()) {
      toast.error("Immatriculation requise");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        license_plate: form.license_plate.trim(),
        brand: form.brand.trim(),
        model: form.model.trim(),
        year: form.year ? parseInt(form.year) : null,
        mileage: form.mileage ? parseInt(form.mileage) : null,
        van_type: form.van_type,
        site: form.site,
        rental_type: form.rental_type,
        rental_type_custom: form.rental_type === "autre" ? form.rental_type_custom : "",
        monthly_rent: form.monthly_rent ? parseFloat(form.monthly_rent) : 0,
        daily_revenue: form.daily_revenue ? parseFloat(form.daily_revenue) : 200,
        rental_agency: form.rental_agency.trim(),
      };
      await updateTruck(truck.id, payload);
      toast.success("Camion mis à jour");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur lors de la mise à jour";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const VAN_TYPES = ["trafic", "expert", "jumpy", "vivaro", "transit", "generic"];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        onClick={() => setOpen(true)}
        className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 hover:bg-zinc-900 hover:text-white inline-flex items-center gap-1.5"
        data-testid="edit-truck-info-btn"
      >
        <PencilSimple size={12} weight="bold" /> Modifier les infos
      </button>
      <DialogContent className="rounded-none border border-zinc-900 max-w-lg">
        <DialogHeader>
          <DialogTitle
            className="text-2xl font-black uppercase tracking-tight"
            style={{ fontFamily: "Chivo" }}
          >
            Modifier le véhicule
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Mise à jour des informations sans avoir à recréer le camion.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div className="col-span-2">
            <Label className="label-tag">Immatriculation</Label>
            <Input
              data-testid="edit-license-plate"
              className="rounded-none border-zinc-900 mono uppercase"
              value={form.license_plate}
              onChange={(e) => setForm({ ...form, license_plate: e.target.value.toUpperCase() })}
            />
          </div>
          <div>
            <Label className="label-tag">Marque</Label>
            <Input
              data-testid="edit-brand"
              className="rounded-none border-zinc-900"
              value={form.brand}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
              placeholder="Renault"
            />
          </div>
          <div>
            <Label className="label-tag">Modèle</Label>
            <Input
              data-testid="edit-model"
              className="rounded-none border-zinc-900"
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              placeholder="Trafic"
            />
          </div>
          <div>
            <Label className="label-tag">Année</Label>
            <Input
              data-testid="edit-year"
              type="number"
              className="rounded-none border-zinc-900 mono"
              value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value })}
              placeholder="2023"
            />
          </div>
          <div>
            <Label className="label-tag">Kilométrage</Label>
            <Input
              data-testid="edit-mileage"
              type="number"
              className="rounded-none border-zinc-900 mono"
              value={form.mileage}
              onChange={(e) => setForm({ ...form, mileage: e.target.value })}
              placeholder="125000"
            />
          </div>
          <div>
            <Label className="label-tag">Type 3D</Label>
            <Select value={form.van_type} onValueChange={(v) => setForm({ ...form, van_type: v })}>
              <SelectTrigger className="rounded-none border-zinc-900" data-testid="edit-van-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {VAN_TYPES.map((v) => (
                  <SelectItem key={v} value={v}>{v.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tag">Site</Label>
            <Select value={form.site} onValueChange={(v) => setForm({ ...form, site: v })}>
              <SelectTrigger className="rounded-none border-zinc-900" data-testid="edit-site">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {sitesList.map((s) => (
                  <SelectItem key={s.code} value={s.code}>
                    {s.name ? `${s.code} · ${s.name}` : s.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="label-tag">Propriétaire</Label>
            <Select value={form.rental_type} onValueChange={(v) => setForm({ ...form, rental_type: v })}>
              <SelectTrigger className="rounded-none border-zinc-900" data-testid="edit-rental-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="own">NY TRANS</SelectItem>
                {owners.filter((o) => o.key !== "own" && (o.label || "").toLowerCase() !== "à nous").map((o) => (
                  <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                ))}
                <SelectItem value="autre">Autre…</SelectItem>
              </SelectContent>
            </Select>
            {form.rental_type === "autre" && (
              <Input
                className="rounded-none border-zinc-900 mt-2"
                value={form.rental_type_custom}
                onChange={(e) => setForm({ ...form, rental_type_custom: e.target.value })}
                placeholder="Nom du loueur / propriétaire"
                data-testid="edit-rental-custom"
              />
            )}
          </div>
          {/* ---- Finance / Budget ---- */}
          <div className="col-span-2 border-t border-dashed border-zinc-300 pt-3">
            <Label className="label-tag block mb-2">Finance</Label>
            <div className="grid grid-cols-2 gap-3">
              {form.rental_type !== "own" && (
                <>
                  <div className="col-span-2">
                    <Label className="label-tag">Contact agence</Label>
                    <Input
                      data-testid="edit-rental-agency"
                      className="rounded-none border-zinc-900"
                      value={form.rental_agency}
                      onChange={(e) => setForm({ ...form, rental_agency: e.target.value })}
                      placeholder="contact@sixt.fr ou 01 23 45 67 89"
                    />
                  </div>
                  <div>
                    <Label className="label-tag">Loyer mensuel (€)</Label>
                    <Input
                      data-testid="edit-monthly-rent"
                      type="number"
                      className="rounded-none border-zinc-900 mono"
                      value={form.monthly_rent}
                      onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })}
                      placeholder="800"
                    />
                  </div>
                </>
              )}
              <div className={form.rental_type !== "own" ? "" : "col-span-2"}>
                <Label className="label-tag">CA journalier (€/jour)</Label>
                <Input
                  data-testid="edit-daily-revenue"
                  type="number"
                  className="rounded-none border-zinc-900 mono"
                  value={form.daily_revenue}
                  onChange={(e) => setForm({ ...form, daily_revenue: e.target.value })}
                  placeholder="200"
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="rounded-none border-zinc-900"
            data-testid="edit-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="rounded-none bg-zinc-900 text-white hover:bg-zinc-700"
            data-testid="edit-save-btn"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TruckDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [truck, setTruck] = useState(null);
  const [impacts, setImpacts] = useState([]);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [usageHistory, setUsageHistory] = useState({ stints: [], total_stints: 0, total_days: 0, total_km: 0 });
  const [appointments, setAppointments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoUploadProgress, setPhotoUploadProgress] = useState(null); // { done, total }

  const [pickPosition, setPickPosition] = useState(null);
  const [impactDialog, setImpactDialog] = useState(false);
  const [editingImpact, setEditingImpact] = useState(null);
  const [activeImpactId, setActiveImpactId] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [lightbox, setLightbox] = useState({ list: [], index: null });
  const [rdvDialogOpen, setRdvDialogOpen] = useState(false);
  const [rdvForm, setRdvForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    time: "10:00",
    type: "autre",
    title: "",
    location: "",
    notes: "",
    cost: "",
    category: "entretien",
    invoice_doc: null,
  });
  const [rdvUploading, setRdvUploading] = useState(false);

  const uploadRdvInvoice = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 25MB)");
      return;
    }
    setRdvUploading(true);
    try {
      const toSend = file.type && file.type.startsWith("image/") ? await compressImage(file) : file;
      const { path } = await uploadPhoto(toSend);
      setRdvForm((f) => ({ ...f, invoice_doc: path }));
      toast.success("Facture ajoutée");
    } catch {
      toast.error("Erreur upload facture");
    } finally {
      setRdvUploading(false);
    }
  };

  const submitRdv = async () => {
    if (!rdvForm.date) return toast.error("Date requise");
    try {
      await createAppointment({
        truck_id: id,
        date: new Date(`${rdvForm.date}T${rdvForm.time}:00`).toISOString(),
        type: rdvForm.type,
        title: rdvForm.title,
        location: rdvForm.location,
        notes: rdvForm.notes,
        cost: rdvForm.cost ? parseFloat(rdvForm.cost) : 0,
        category: rdvForm.category || null,
        invoice_doc: rdvForm.invoice_doc || null,
      });
      toast.success("RDV ajouté au calendrier");
      setRdvDialogOpen(false);
      setRdvForm({
        date: new Date().toISOString().slice(0, 10),
        time: "10:00",
        type: "autre",
        title: "",
        location: "",
        notes: "",
        cost: "",
        category: "entretien",
        invoice_doc: null,
      });
      // Refresh appointments + history (RDV adds a "planifié" entry)
      try {
        const [appts, hist] = await Promise.all([
          fetchAppointments(id).catch(() => []),
          fetchHistory(id).catch(() => []),
        ]);
        setAppointments(appts);
        setHistory(hist);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("appt refresh failed:", e);
        }
      }
    } catch {
      toast.error("Erreur");
    }
  };

  const openLightbox = React.useCallback((list, index) => {
    setLightbox({ list, index });
  }, []);
  const closeLightbox = React.useCallback(() => {
    setLightbox({ list: [], index: null });
  }, []);
  const prevLightbox = React.useCallback(() => {
    setLightbox((lb) =>
      lb.index == null
        ? lb
        : { ...lb, index: (lb.index - 1 + lb.list.length) % lb.list.length }
    );
  }, []);
  const nextLightbox = React.useCallback(() => {
    setLightbox((lb) =>
      lb.index == null ? lb : { ...lb, index: (lb.index + 1) % lb.list.length }
    );
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [t, imp, com, hist, drv, own, appts, usage] = await Promise.all([
        fetchTruck(id),
        fetchImpacts(id),
        fetchComments(id),
        fetchHistory(id),
        fetchDriverNames(),
        api.get(`/owners`).then((r) => r.data).catch(() => []),
        fetchAppointments(id).catch(() => []),
        fetchTruckUsageHistory(id).catch(() => ({ stints: [], total_stints: 0, total_days: 0, total_km: 0 })),
      ]);
      setTruck(t);
      setImpacts(imp);
      setComments(com);
      setHistory(hist);
      setDrivers(drv);
      setOwners(own);
      setAppointments(appts);
      setUsageHistory(usage);
    } catch (e) {
      toast.error("Camion introuvable");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [id]);

  const handleAddImpact = React.useCallback((pos) => {
    setPickPosition(pos);
    setEditingImpact(null);
    setImpactDialog(true);
  }, []);

  const saveImpact = async (data) => {
    try {
      if (editingImpact) {
        const updated = await updateImpact(editingImpact.id, data);
        setImpacts((arr) => arr.map((i) => (i.id === updated.id ? updated : i)));
        toast.success("Impact mis à jour");
      } else {
        const created = await createImpact(id, { ...data, position: pickPosition });
        setImpacts((arr) => [created, ...arr]);
        toast.success("Impact enregistré sur le véhicule");
      }
      // refresh driver autocomplete
      try {
        const drv = await fetchDriverNames();
        setDrivers(drv);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("driver refresh failed:", e);
        }
      }
      setImpactDialog(false);
      setEditingImpact(null);
      setPickPosition(null);
    } catch (e) {
      toast.error("Erreur enregistrement impact");
    }
  };

  const removeImpact = async (impId) => {
    try {
      await deleteImpact(impId);
      setImpacts((arr) => arr.filter((i) => i.id !== impId));
      toast.success("Impact supprimé");
    } catch {
      toast.error("Erreur suppression");
    }
  };

  const toggleStatus = async (newStatus, extraFields = {}) => {
    try {
      const t = await updateTruck(id, { status: newStatus, ...extraFields });
      setTruck(t);
      const hist = await fetchHistory(id);
      setHistory(hist);
      toast.success(`Statut mis à jour : ${newStatus}`);
    } catch {
      toast.error("Erreur");
    }
  };

  const setStatusActif = () => {
    // If the truck is currently EN PANNE, prompt for repair confirmation.
    // The user can validate without providing any info, so we don't block.
    if (truck?.status === "panne") {
      const entries = Array.isArray(truck.panne_entries) ? truck.panne_entries : [];
      // If there are no entries but a legacy panne_reason exists, synthesize one for the dialog
      const items = entries.length > 0
        ? entries
        : (truck.panne_reason
            ? [{
                id: "legacy",
                description: truck.panne_reason,
                date: truck.panne_date ? truck.panne_date.slice(0, 10) : "",
                severity: truck.panne_severity || "mineur",
                repaired: false,
                repair_cost: 0,
              }]
            : []);
      setResumeActifItems(items.map((it) => ({
        id: it.id || `syn-${Math.random().toString(36).slice(2, 8)}`,
        description: it.description || "",
        date: it.date || "",
        severity: it.severity || "mineur",
        repaired: !!it.repaired,
        repair_cost: it.repair_cost || 0,
      })));
      setResumeActifOpen(true);
      return;
    }
    toggleStatus("actif", { panne_reason: "", panne_severity: null });
  };
  const setStatusInactif = () => toggleStatus("inactif");

  // -------- Resume from PANNE → ACTIF dialog (multi-panne repair) --------
  const [resumeActifOpen, setResumeActifOpen] = useState(false);
  const [resumeActifItems, setResumeActifItems] = useState([]);

  const updateResumeItem = (id, patch) =>
    setResumeActifItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const confirmResumeWithInfo = async () => {
    // Persist the updated panne_entries (with repaired/repair_cost) as part of the
    // status transition. Backend snapshots them into the BreakdownEvent.
    const cleaned = resumeActifItems.map((it) => ({
      id: it.id && !it.id.startsWith("syn-") && it.id !== "legacy" ? it.id : undefined,
      description: it.description || "",
      date: it.date || null,
      severity: it.severity === "majeur" ? "majeur" : "mineur",
      repaired: !!it.repaired,
      repair_cost: parseFloat(it.repair_cost || 0) || 0,
    }));
    await toggleStatus("actif", {
      panne_reason: "",
      panne_severity: null,
      panne_entries: cleaned,
    });
    setResumeActifOpen(false);
  };

  const confirmResumeWithoutInfo = async () => {
    await toggleStatus("actif", { panne_reason: "", panne_severity: null });
    setResumeActifOpen(false);
  };

  const todayStr = () => {
    const d = new Date();
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d - tz).toISOString().slice(0, 10);
  };

  const [renduDialogOpen, setRenduDialogOpen] = useState(false);
  const [renduDate, setRenduDate] = useState("");

  const requestRendu = () => {
    setRenduDate(
      truck?.rendu_date ? truck.rendu_date.slice(0, 10) : todayStr()
    );
    setRenduDialogOpen(true);
  };

  const confirmRendu = async () => {
    if (!renduDate) {
      toast.error("Date requise");
      return;
    }
    await toggleStatus("rendu", {
      rendu_date: `${renduDate}T12:00:00Z`,
    });
    setRenduDialogOpen(false);
  };

  const [panneDialogOpen, setPanneDialogOpen] = useState(false);
  const [panneReason, setPanneReason] = useState("");
  const [panneDate, setPanneDate] = useState("");
  const [panneSeverity, setPanneSeverity] = useState("mineur");

  const requestPanne = () => {
    setPanneReason(truck?.panne_reason || "");
    setPanneDate(
      truck?.panne_date ? truck.panne_date.slice(0, 10) : todayStr()
    );
    setPanneSeverity(truck?.panne_severity || "mineur");
    setPanneDialogOpen(true);
  };

  const confirmPanne = async () => {
    if (!panneReason.trim()) {
      toast.error("Motif requis");
      return;
    }
    if (!panneDate) {
      toast.error("Date requise");
      return;
    }
    // Seed panne_entries with the primary panne so the multi-panne UI is consistent.
    const existing = Array.isArray(truck?.panne_entries) ? truck.panne_entries : [];
    const seededEntry = {
      description: panneReason.trim(),
      date: panneDate,
      severity: panneSeverity,
      repaired: false,
      repair_cost: 0,
    };
    // If we are entering panne for the first time, start with a single entry.
    // If a panne was already ongoing, preserve any additional entries.
    const nextEntries = truck?.status === "panne" ? [seededEntry, ...existing] : [seededEntry];
    await toggleStatus("panne", {
      panne_reason: panneReason.trim(),
      panne_date: `${panneDate}T12:00:00Z`,
      panne_severity: panneSeverity,
      panne_entries: nextEntries,
    });
    setPanneDialogOpen(false);
  };

  // -------- Add an extra panne to an already-broken truck --------
  const [addPanneOpen, setAddPanneOpen] = useState(false);
  const [addPanneDesc, setAddPanneDesc] = useState("");
  const [addPanneDate, setAddPanneDate] = useState("");
  const [addPanneSev, setAddPanneSev] = useState("mineur");

  const openAddPanne = () => {
    setAddPanneDesc("");
    setAddPanneDate(todayStr());
    setAddPanneSev("mineur");
    setAddPanneOpen(true);
  };

  const confirmAddPanne = async () => {
    if (!addPanneDesc.trim()) {
      toast.error("Motif requis");
      return;
    }
    const newEntry = {
      description: addPanneDesc.trim(),
      date: addPanneDate || todayStr(),
      severity: addPanneSev,
      repaired: false,
      repair_cost: 0,
    };
    const current = Array.isArray(truck?.panne_entries) ? truck.panne_entries : [];
    try {
      const t = await updateTruck(id, { panne_entries: [...current, newEntry] });
      setTruck(t);
      toast.success("Panne ajoutée");
      setAddPanneOpen(false);
    } catch {
      toast.error("Erreur");
    }
  };

  const removePanneEntry = async (entryId) => {
    if (!window.confirm("Supprimer cette panne ?")) return;
    const current = Array.isArray(truck?.panne_entries) ? truck.panne_entries : [];
    try {
      const t = await updateTruck(id, {
        panne_entries: current.filter((e) => e.id !== entryId),
      });
      setTruck(t);
      toast.success("Panne retirée");
    } catch {
      toast.error("Erreur");
    }
  };

  const [redTagDialogOpen, setRedTagDialogOpen] = useState(false);
  const [redTagReason, setRedTagReason] = useState("");

  const requestRedTagOn = () => {
    setRedTagReason(truck?.red_tag_reason || "");
    setRedTagDialogOpen(true);
  };

  const confirmRedTag = async () => {
    if (!redTagReason.trim()) {
      toast.error("Détails requis");
      return;
    }
    try {
      const t = await updateTruck(id, {
        red_tag: true,
        red_tag_reason: redTagReason.trim(),
      });
      setTruck(t);
      const hist = await fetchHistory(id);
      setHistory(hist);
      setRedTagDialogOpen(false);
      toast.error("RED TAG activé — refusé par Amazon");
    } catch {
      toast.error("Erreur");
    }
  };

  const toggleRedTag = async (val) => {
    if (val) {
      requestRedTagOn();
      return;
    }
    try {
      const t = await updateTruck(id, { red_tag: false, red_tag_reason: "" });
      setTruck(t);
      const hist = await fetchHistory(id);
      setHistory(hist);
      toast.success("Red Tag retiré");
    } catch {
      toast.error("Erreur");
    }
  };

  const [expertDialogOpen, setExpertDialogOpen] = useState(false);
  const [expertDate, setExpertDate] = useState("");

  const requestExpertOn = () => {
    setExpertDate(truck?.expert_visit_date ? truck.expert_visit_date.slice(0, 10) : "");
    setExpertDialogOpen(true);
  };

  const confirmExpertOn = async () => {
    if (!expertDate) {
      toast.error("Date requise");
      return;
    }
    try {
      const t = await updateTruck(id, {
        awaiting_expert: true,
        expert_visit_date: `${expertDate}T12:00:00Z`,
        expert_visited: false,
      });
      setTruck(t);
      const hist = await fetchHistory(id);
      setHistory(hist);
      // Auto-create an Expert appointment in the calendar (idempotent on same date)
      try {
        const existing = await fetchAppointments(id);
        const sameDay = existing.find(
          (a) =>
            a.type === "expert" &&
            a.status === "planifie" &&
            (a.date || "").slice(0, 10) === expertDate
        );
        if (!sameDay) {
          await createAppointment({
            truck_id: id,
            date: new Date(`${expertDate}T10:00:00`).toISOString(),
            type: "expert",
            title: `Passage expert · ${truck?.license_plate || ""}`.trim(),
          });
        }
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("expert RDV auto-create failed:", e);
        }
      }
      // Refresh appointments list so the new expert RDV shows on the card
      try {
        const appts = await fetchAppointments(id);
        setAppointments(appts);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("appt refresh after expert toggle failed:", e);
        }
      }
      setExpertDialogOpen(false);
      toast.success("En attente d'expert · RDV ajouté au calendrier");
    } catch {
      toast.error("Erreur");
    }
  };

  const toggleExpert = async (val) => {
    if (val) {
      requestExpertOn();
      return;
    }
    try {
      const t = await updateTruck(id, {
        awaiting_expert: false,
        expert_visit_date: null,
        expert_visited: false,
      });
      setTruck(t);
      const hist = await fetchHistory(id);
      setHistory(hist);
      toast.success("Plus d'attente d'expert");
    } catch {
      toast.error("Erreur");
    }
  };

  const markExpertVisited = async () => {
    try {
      const t = await updateTruck(id, {
        expert_visited: true,
        awaiting_expert: false,
      });
      setTruck(t);
      const hist = await fetchHistory(id);
      setHistory(hist);
      toast.success("Expert passé — enregistré");
    } catch {
      toast.error("Erreur");
    }
  };

  const handleExportPDF = async () => {
    try {
      await exportTruckReport({ truck, impacts, comments, history });
      toast.success("Rapport PDF généré");
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.error("PDF export failed:", e);
      }
      toast.error("Erreur génération PDF");
    }
  };

  const addCommentNow = async () => {
    if (!commentText.trim()) return;
    try {
      const c = await createComment(id, { content: commentText });
      setComments((arr) => [c, ...arr]);
      setCommentText("");
      toast.success("Commentaire ajouté");
    } catch {
      toast.error("Erreur");
    }
  };

  const removeComment = async (cid) => {
    try {
      await deleteComment(cid);
      setComments((arr) => arr.filter((c) => c.id !== cid));
    } catch {
      toast.error("Erreur");
    }
  };

  const uploadPhotoToTruck = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    input.value = "";
    if (files.length === 0) return;
    const MAX_PER_BATCH = 20;
    const batch = files.slice(0, MAX_PER_BATCH);
    if (files.length > MAX_PER_BATCH) {
      toast.message(`${MAX_PER_BATCH} photos max par envoi`);
    }
    setPhotoUploadProgress({ done: 0, total: batch.length });
    let ok = 0;
    let failed = 0;
    let latestTruck = null;
    for (let i = 0; i < batch.length; i++) {
      const file = batch[i];
      try {
        if (file.size > 25 * 1024 * 1024) {
          failed += 1;
        } else {
          const compressed = await compressImage(file);
          const { path } = await uploadPhoto(compressed);
          latestTruck = await addPhoto(id, path);
          ok += 1;
        }
      } catch {
        failed += 1;
      }
      setPhotoUploadProgress({ done: i + 1, total: batch.length });
      // Yield to the browser so iOS Safari can repaint between heavy ops
      // (prevents the "écran noir / flash" effect users reported).
      await new Promise((r) => setTimeout(r, 0));
    }
    if (latestTruck) setTruck(latestTruck);
    setPhotoUploadProgress(null);
    if (ok > 0 && failed === 0) toast.success(ok > 1 ? `${ok} photos ajoutées` : "Photo ajoutée");
    else if (ok > 0 && failed > 0) toast.warning(`${ok} ajoutée(s), ${failed} échec(s)`);
    else toast.error("Aucune photo n'a pu être ajoutée");
  };

  const removePhoto = async (index) => {
    try {
      const t = await deletePhoto(id, index);
      setTruck(t);
    } catch {
      toast.error("Erreur");
    }
  };

  const removeTruck = async () => {
    try {
      await deleteTruck(id);
      toast.success("Camion supprimé");
      navigate("/");
    } catch {
      toast.error("Erreur");
    }
  };

  if (loading || !truck) {
    return (
      <div className="max-w-7xl mx-auto p-8 text-center" data-testid="loading-detail">
        Chargement…
      </div>
    );
  }

  const status = STATUS_LABEL[truck.status];
  const totalCost = impacts.reduce((s, i) => s + (i.cost || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-6" data-testid="truck-detail-page">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:underline"
          data-testid="back-link"
        >
          <ArrowLeft size={16} weight="bold" /> Retour
        </Link>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setRdvDialogOpen(true)}
            className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-900 bg-white text-zinc-900 hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2 transition-none"
            data-testid="open-rdv-btn"
          >
            <Calendar size={14} weight="bold" /> Nouveau RDV
          </button>
          <button
            onClick={() => navigate(`/sinistres?truck=${truck.id}`)}
            className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-red-600 bg-white text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center gap-2 transition-none"
            data-testid="declare-sinistre-btn"
          >
            <WarningOctagon size={14} weight="bold" /> Déclarer sinistre
          </button>
          <button
            onClick={handleExportPDF}
            className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-900 bg-white text-zinc-900 hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2 transition-none"
            data-testid="export-pdf-btn"
          >
            <FilePdf size={14} weight="bold" /> Export PDF
          </button>
          <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-red-600 text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center gap-2"
              data-testid="delete-truck-btn"
            >
              <Trash size={14} weight="bold" /> Supprimer camion
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-none border border-zinc-900">
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce camion ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action supprimera également tous les impacts, commentaires et
                photos liés. Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none border-zinc-900">
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={removeTruck}
                className="rounded-none bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-truck"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border border-zinc-900 p-5 mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-4 flex-wrap">
          {/* VIN + QR chip — always visible next to the plate */}
          <div
            className="w-24 h-24 shrink-0 border-2 border-zinc-900 bg-white flex items-center justify-center p-1"
            data-testid="header-vin-qr"
            title={truck.vin ? `VIN : ${truck.vin}` : "Aucun VIN — ajoute la carte grise pour le générer"}
          >
            {truck.vin ? (
              <QRCode
                value={truck.vin}
                size={88}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                viewBox="0 0 256 256"
              />
            ) : (
              <div className="flex flex-col items-center gap-1 text-center">
                <QrCode size={28} weight="bold" className="text-zinc-300" />
                <span className="text-[8px] uppercase tracking-widest text-zinc-400 font-bold leading-tight">
                  Ajoute la<br />carte grise
                </span>
              </div>
            )}
          </div>
          <div>
            <div className="label-tag">{truck.brand} {truck.model}</div>
            <h1
              className="text-4xl font-black uppercase tracking-tighter mono"
              style={{ fontFamily: "Chivo" }}
              data-testid="truck-plate"
            >
              {truck.license_plate}
            </h1>
            {truck.vin && (
              <div
                className="mono text-[10px] font-bold tracking-widest text-zinc-500 mt-1"
                data-testid="truck-vin-inline"
              >
                VIN · {truck.vin}
              </div>
            )}
            <div className="mt-1">
              <EditTruckInfoDialog truck={truck} owners={owners} onSaved={loadAll} />
            </div>
          <div className="flex gap-2 mt-2 flex-wrap">
            <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${status.cls}`} data-testid="current-status-badge">
              {status.label}
            </span>
            {truck.status === "panne" && truck.panne_severity && (
              <span
                className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border ${
                  truck.panne_severity === "majeur"
                    ? "bg-red-600 text-white border-red-700"
                    : "bg-amber-500 text-white border-amber-600"
                }`}
                data-testid="panne-severity-badge"
              >
                {truck.panne_severity === "majeur" ? "⛔ MAJEURE" : "⚙ MINEURE"}
              </span>
            )}
            <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-zinc-100 border border-zinc-300 inline-flex items-center gap-1">
              <MapPin size={10} weight="bold" /> {SITE_LABEL[truck.site] || truck.site}
            </span>
            <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-zinc-100 border border-zinc-300 inline-flex items-center gap-1" data-testid="truck-owner-badge">
              <Tag size={10} weight="bold" />
              {(() => {
                const rt = truck.rental_type;
                if (rt === "autre" && truck.rental_type_custom) return truck.rental_type_custom;
                const dyn = owners.find((o) => o.key === rt);
                if (dyn) return dyn.label;
                return RENTAL_LABEL[rt] || rt || "—";
              })()}
            </span>
            {truck.site === "DLP4" && <TruckTollBadge truckId={truck.id} />}
            {truck.red_tag && (
              <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest status-red-tag" data-testid="red-tag-badge">
                RED TAG · refusé Amazon
              </span>
            )}
            {truck.awaiting_expert && (
              <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest status-expert" data-testid="expert-badge">
                Att. expert {truck.expert_visit_date ? new Date(truck.expert_visit_date).toLocaleDateString("fr-FR") : ""}
              </span>
            )}
            {truck.expert_visited && !truck.awaiting_expert && (
              <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-purple-100 border border-purple-300 text-purple-700">
                Expert passé
              </span>
            )}
            <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-zinc-100 border border-zinc-300">
              {impacts.length} impact{impacts.length > 1 ? "s" : ""}
            </span>
            {totalCost > 0 && (
              <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-zinc-100 border border-zinc-300 mono">
                {totalCost.toLocaleString()} € dommages
              </span>
            )}
          </div>
          {truck.status === "panne" && (
            <div className="mt-3 bg-amber-50 border-l-4 border-amber-600 px-3 py-2" data-testid="panne-reason-display">
              <div className="flex items-center justify-between mb-1">
                <div className="label-tag !text-amber-700">
                  Panne{truck.panne_date ? ` · ${new Date(truck.panne_date).toLocaleDateString("fr-FR")}` : ""}
                </div>
                <button
                  type="button"
                  onClick={openAddPanne}
                  className="text-[10px] font-bold uppercase tracking-widest border-2 border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white px-2 py-0.5 inline-flex items-center gap-1"
                  data-testid="add-panne-entry"
                  title="Ajouter une nouvelle panne à ce camion"
                >
                  + Ajouter une panne
                </button>
              </div>
              {(Array.isArray(truck.panne_entries) && truck.panne_entries.length > 0) ? (
                <ul className="mt-1 space-y-1" data-testid="panne-entries-list">
                  {truck.panne_entries.map((e, idx) => (
                    <li
                      key={e.id || idx}
                      className="flex items-start gap-2 text-sm border-t border-amber-200 pt-1 first:border-t-0 first:pt-0"
                      data-testid={`panne-entry-${e.id || idx}`}
                    >
                      <span className="mono text-[10px] text-amber-700 shrink-0 mt-0.5 min-w-[18px]">
                        #{idx + 1}
                      </span>
                      <span className="flex-1">
                        <span className="font-medium">{e.description}</span>
                        {(e.date || e.severity) && (
                          <span className="ml-2 text-[11px] text-amber-700">
                            {e.date ? new Date(e.date).toLocaleDateString("fr-FR") : ""}
                            {e.severity === "majeur" ? " · MAJEURE" : e.severity === "mineur" ? " · mineure" : ""}
                          </span>
                        )}
                        {e.repaired && (
                          <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-emerald-600 text-white">
                            Réparé{e.repair_cost > 0 ? ` · ${e.repair_cost.toFixed(2)}€` : ""}
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => removePanneEntry(e.id)}
                        className="text-amber-500 hover:text-red-600 shrink-0"
                        title="Retirer cette panne"
                        data-testid={`remove-panne-entry-${e.id || idx}`}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : truck.panne_reason ? (
                <div className="text-sm mt-1">{truck.panne_reason}</div>
              ) : null}
            </div>
          )}
          {truck.status === "rendu" && truck.rendu_date && (
            <div className="mt-3 bg-blue-50 border-l-4 border-blue-600 px-3 py-2" data-testid="rendu-date-display">
              <div className="label-tag !text-blue-700">Camion rendu</div>
              <div className="text-sm mt-1 mono">
                Le {new Date(truck.rendu_date).toLocaleDateString("fr-FR")}
              </div>
            </div>
          )}
          {truck.red_tag && truck.red_tag_reason && (
            <div className="mt-3 bg-red-50 border-l-4 border-red-600 px-3 py-2" data-testid="redtag-reason-display">
              <div className="label-tag !text-red-700">Détails RED TAG</div>
              <div className="text-sm mt-1">{truck.red_tag_reason}</div>
            </div>
          )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 min-w-[280px]">
          <div className="bg-zinc-100 border border-zinc-300 p-2">
            <div className="label-tag">Année</div>
            <div className="text-sm font-bold mono">{truck.year || "—"}</div>
          </div>
          <div className="bg-zinc-100 border border-zinc-300 p-2">
            <div className="label-tag">Kilométrage</div>
            <div className="text-sm font-bold mono">
              {truck.mileage ? truck.mileage.toLocaleString() + " km" : "—"}
            </div>
          </div>
          <div className="bg-zinc-100 border border-zinc-300 p-2">
            <div className="label-tag">Marque</div>
            <div className="text-sm font-bold">{truck.brand || "—"}</div>
          </div>
          <div className="bg-zinc-100 border border-zinc-300 p-2">
            <div className="label-tag">Type</div>
            <div className="text-sm font-bold uppercase">{truck.van_type}</div>
          </div>
        </div>
      </div>

      {/* Status actions bar */}
      <div className="bg-zinc-900 text-white p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="label-tag !text-zinc-400">Actions véhicule</div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={setStatusActif}
            disabled={truck.status === "actif"}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border inline-flex items-center gap-2 ${
              truck.status === "actif"
                ? "bg-green-600 border-green-600 text-white"
                : "border-zinc-700 text-zinc-200 hover:bg-green-600 hover:border-green-600"
            }`}
            data-testid="set-actif"
          >
            <CheckCircle size={14} weight="bold" /> Actif
          </button>
          <button
            onClick={setStatusInactif}
            disabled={truck.status === "inactif"}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border inline-flex items-center gap-2 ${
              truck.status === "inactif"
                ? "bg-zinc-500 border-zinc-500 text-white"
                : "border-zinc-700 text-zinc-200 hover:bg-zinc-500 hover:border-zinc-500"
            }`}
            data-testid="set-inactif"
          >
            <Pause size={14} weight="bold" /> Inactif
          </button>
          <button
            onClick={requestRendu}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border inline-flex items-center gap-2 ${
              truck.status === "rendu"
                ? "bg-blue-600 border-blue-600 text-white"
                : "border-zinc-700 text-zinc-200 hover:bg-blue-600 hover:border-blue-600"
            }`}
            data-testid="set-rendu"
          >
            <Package size={14} weight="bold" /> Camion rendu
          </button>
          <button
            onClick={requestPanne}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border inline-flex items-center gap-2 ${
              truck.status === "panne"
                ? "bg-amber-600 border-amber-600 text-white"
                : "border-zinc-700 text-zinc-200 hover:bg-amber-600 hover:border-amber-600"
            }`}
            data-testid="set-panne"
          >
            <Wrench size={14} weight="bold" /> En panne
          </button>
          <div className="h-6 w-px bg-zinc-700 mx-2" />
          <div className="flex items-center gap-2 px-3 py-2 border border-zinc-700">
            <WarningOctagon size={16} weight="fill" className="text-red-500" />
            <span className="text-xs font-bold uppercase tracking-widest">RED TAG</span>
            <Switch
              checked={truck.red_tag}
              onCheckedChange={toggleRedTag}
              data-testid="redtag-switch"
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 border border-zinc-700">
            <Eyeglasses size={16} weight="fill" className="text-purple-400" />
            <span className="text-xs font-bold uppercase tracking-widest">Att. expert</span>
            <Switch
              checked={truck.awaiting_expert}
              onCheckedChange={toggleExpert}
              data-testid="expert-switch"
            />
            {truck.awaiting_expert && (
              <button
                onClick={markExpertVisited}
                className="ml-2 text-[10px] uppercase tracking-widest font-bold bg-purple-500 hover:bg-purple-600 text-white px-2 py-1"
                data-testid="mark-expert-visited"
              >
                Expert passé
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main split: 3D + Right panel */}
      <div className="grid lg:grid-cols-5 gap-4">
        {/* 3D Viewer */}
        <div className="lg:col-span-3 h-[55vh] sm:h-[500px] lg:h-[640px]">
          <VanViewer
            vanType={truck.van_type}
            impacts={impacts}
            onAddImpact={handleAddImpact}
            activeImpactId={activeImpactId}
            onSelectImpact={setActiveImpactId}
          />
        </div>

        {/* Right panel tabs */}
        <div className="lg:col-span-2">
          <TruckLocationCard
            truck={truck}
            onUpdated={(updated) => setTruck(updated)}
          />
          <UpcomingAppointmentsCard
            appointments={appointments}
            onValidate={async (a) => {
              try {
                await updateAppointment(a.id, { status: "fait" });
                toast.success("RDV validé · ajouté à l'historique");
                const [hist, appts] = await Promise.all([
                  fetchHistory(id),
                  fetchAppointments(id).catch(() => []),
                ]);
                setHistory(hist);
                setAppointments(appts);
              } catch {
                toast.error("Erreur");
              }
            }}
            onCancel={async (a) => {
              if (!window.confirm("Annuler ce RDV ?")) return;
              try {
                await updateAppointment(a.id, { status: "annule" });
                toast.success("RDV annulé");
                const [hist, appts] = await Promise.all([
                  fetchHistory(id),
                  fetchAppointments(id).catch(() => []),
                ]);
                setHistory(hist);
                setAppointments(appts);
              } catch {
                toast.error("Erreur");
              }
            }}
            onNew={() => setRdvDialogOpen(true)}
            onSeeCalendar={() => navigate("/calendrier")}
          />
          <Tabs defaultValue="impacts" className="w-full">
            <TabsList className="grid grid-cols-6 w-full rounded-none bg-white border border-zinc-900 p-0 h-auto">
              <TabsTrigger
                value="impacts"
                className="rounded-none border-r border-zinc-900 last:border-r-0 data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-[10px] uppercase tracking-widest font-bold py-3 px-1"
                data-testid="tab-impacts"
              >
                Impacts · {impacts.length}
              </TabsTrigger>
              <TabsTrigger
                value="comments"
                className="rounded-none border-r border-zinc-900 last:border-r-0 data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-[10px] uppercase tracking-widest font-bold py-3 px-1"
                data-testid="tab-comments"
              >
                Comm. · {comments.length}
              </TabsTrigger>
              <TabsTrigger
                value="photos"
                className="rounded-none border-r border-zinc-900 last:border-r-0 data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-[10px] uppercase tracking-widest font-bold py-3 px-1"
                data-testid="tab-photos"
              >
                Photos · {truck.photos?.length || 0}
              </TabsTrigger>
              <TabsTrigger
                value="usage"
                className="rounded-none border-r border-zinc-900 last:border-r-0 data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-[10px] uppercase tracking-widest font-bold py-3 px-1 whitespace-nowrap"
                data-testid="tab-usage"
              >
                Usage · {usageHistory.total_stints || 0}
              </TabsTrigger>
              <TabsTrigger
                value="documents"
                className="rounded-none border-r border-zinc-900 last:border-r-0 data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-[10px] uppercase tracking-widest font-bold py-3 px-1"
                data-testid="tab-documents"
              >
                Docs
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="rounded-none data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-[10px] uppercase tracking-widest font-bold py-3 px-1"
                data-testid="tab-history"
              >
                Audit · {history.length}
              </TabsTrigger>
            </TabsList>

            {/* Impacts */}
            <TabsContent
              value="impacts"
              className="mt-3 bg-white border border-zinc-900 p-3 max-h-[580px] overflow-y-auto"
              data-testid="impacts-panel"
            >
              {impacts.length === 0 && (
                <div className="text-center py-10 text-zinc-500">
                  <WarningOctagon size={36} weight="duotone" className="mx-auto mb-2" />
                  <div className="text-sm font-semibold">Aucun impact enregistré</div>
                  <div className="text-xs mt-1">
                    Cliquez sur le camion 3D pour marquer un point.
                  </div>
                </div>
              )}
              {impacts.length > 0 && (
                <ImpactsByDriverPanel impacts={impacts} drivers={drivers} />
              )}
              <div className="space-y-2">
                {impacts.map((imp) => {
                  const matched = drivers.find((d) => d.name === imp.driver);
                  return (
                  <ImpactCard
                    key={imp.id}
                    impact={imp}
                    isActive={activeImpactId === imp.id}
                    driverPhone={matched?.phone || ""}
                    truckPlate={truck?.license_plate || ""}
                    onSelect={() => setActiveImpactId(imp.id)}
                    onEdit={() => {
                      setEditingImpact(imp);
                      setPickPosition(imp.position);
                      setImpactDialog(true);
                    }}
                    onDelete={() => removeImpact(imp.id)}
                    onPhotoOpen={(idx = 0) => {
                      const list = (Array.isArray(imp.photos) && imp.photos.length > 0)
                        ? imp.photos
                        : (imp.photo ? [imp.photo] : []);
                      if (list.length > 0) openLightbox(list, idx);
                    }}
                  />
                  );
                })}
              </div>
            </TabsContent>

            {/* Comments */}
            <TabsContent
              value="comments"
              className="mt-3 bg-white border border-zinc-900 p-3"
              data-testid="comments-panel"
            >
              <div className="flex gap-2 mb-3">
                <Textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Ajouter un commentaire (état, observation, rappel…)"
                  className="rounded-none border-zinc-900"
                  data-testid="comment-input"
                />
              </div>
              <Button
                onClick={addCommentNow}
                className="rounded-none bg-zinc-900 text-white hover:bg-zinc-700 w-full mb-3"
                data-testid="add-comment-btn"
              >
                <Plus size={14} weight="bold" /> Ajouter
              </Button>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {comments.length === 0 && (
                  <div className="text-center py-6 text-zinc-500 text-sm">
                    <ChatCircle size={28} weight="duotone" className="mx-auto mb-1" />
                    Pas de commentaires.
                  </div>
                )}
                {comments.map((c) => (
                  <div
                    key={c.id}
                    className="border border-zinc-200 p-3 bg-zinc-50"
                    data-testid={`comment-${c.id}`}
                  >
                    <div className="flex items-center justify-between text-xs text-zinc-500 mono">
                      <span>{c.author}</span>
                      <div className="flex items-center gap-2">
                        <span>{new Date(c.date).toLocaleString("fr-FR")}</span>
                        <button
                          onClick={() => removeComment(c.id)}
                          className="hover:text-red-600"
                          data-testid={`delete-comment-${c.id}`}
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-wrap">{c.content}</div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Photos */}
            <TabsContent
              value="photos"
              className="mt-3 bg-white border border-zinc-900 p-3"
              data-testid="photos-panel"
            >
              <div className="grid grid-cols-2 gap-2 mb-3">
                <label
                  className={`flex items-center justify-center gap-2 bg-zinc-900 text-white py-3 px-3 text-xs uppercase tracking-widest font-bold hover:bg-zinc-700 ${photoUploadProgress ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
                  data-testid="photo-upload-camera-label"
                >
                  <Camera size={16} weight="bold" />
                  Photo
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={uploadPhotoToTruck}
                    disabled={!!photoUploadProgress}
                    className="hidden"
                    data-testid="photo-upload-camera-input"
                  />
                </label>
                <label
                  className={`flex items-center justify-center gap-2 bg-white border border-zinc-900 text-zinc-900 py-3 px-3 text-xs uppercase tracking-widest font-bold hover:bg-zinc-100 ${photoUploadProgress ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
                  data-testid="photo-upload-label"
                >
                  Galerie (multi)
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={uploadPhotoToTruck}
                    disabled={!!photoUploadProgress}
                    className="hidden"
                    data-testid="photo-upload-input"
                  />
                </label>
              </div>
              {photoUploadProgress && (
                <div
                  className="mb-3 border border-zinc-900 bg-zinc-50 px-3 py-2 text-[11px] font-bold uppercase tracking-widest mono flex items-center justify-between"
                  data-testid="photo-upload-progress"
                >
                  <span>Envoi en cours…</span>
                  <span>
                    {photoUploadProgress.done} / {photoUploadProgress.total}
                  </span>
                </div>
              )}
              {(!truck.photos || truck.photos.length === 0) && (
                <div className="text-center py-6 text-zinc-500 text-sm">
                  <Camera size={28} weight="duotone" className="mx-auto mb-1" />
                  Aucune photo.
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {truck.photos?.map((p, idx) => (
                  <div key={idx} className="relative group border border-zinc-300">
                    <img
                      src={photoUrl(p)}
                      alt={`photo-${idx}`}
                      className="w-full h-32 object-cover cursor-zoom-in"
                      onClick={() => openLightbox(truck.photos, idx)}
                      data-testid={`truck-photo-${idx}`}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removePhoto(idx);
                      }}
                      className="absolute top-1 right-1 bg-red-600 text-white p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                      data-testid={`delete-photo-${idx}`}
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Documents */}
            <TabsContent
              value="documents"
              className="mt-3 bg-white border border-zinc-900 p-3 max-h-[580px] overflow-y-auto"
              data-testid="documents-panel"
            >
              <DocumentsPanel truck={truck} onSaved={loadAll} />
            </TabsContent>

            {/* Usage — driver history from daily assignments */}
            <TabsContent
              value="usage"
              className="mt-3 bg-white border border-zinc-900 p-3 max-h-[580px] overflow-y-auto"
              data-testid="usage-panel"
            >
              <UsageHistoryPanel usage={usageHistory} />
            </TabsContent>

            {/* History */}
            <TabsContent
              value="history"
              className="mt-3 bg-white border border-zinc-900 p-3 max-h-[580px] overflow-y-auto"
              data-testid="history-panel"
            >
              <HistoryTimeline history={history} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <ImpactDialog
        open={impactDialog}
        position={pickPosition}
        editing={editingImpact}
        drivers={drivers}
        onDriverCreated={async () => {
          try {
            const drv = await fetchDriverNames();
            setDrivers(drv);
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.warn("driver list refresh failed:", e);
            }
          }
        }}
        onClose={() => {
          setImpactDialog(false);
          setEditingImpact(null);
          setPickPosition(null);
        }}
        onSave={saveImpact}
      />

      {/* Panne reason dialog */}
      <Dialog open={panneDialogOpen} onOpenChange={setPanneDialogOpen}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-md">
          <DialogHeader>
            <DialogTitle
              className="text-2xl font-black uppercase tracking-tight inline-flex items-center gap-2"
              style={{ fontFamily: "Chivo" }}
            >
              <Wrench size={22} weight="fill" className="text-amber-600" /> Mettre en panne
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Décrivez le motif de la panne (sera enregistré dans le rapport).
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="label-tag">Sévérité *</Label>
            <div className="flex gap-2 mt-1">
              <button
                type="button"
                onClick={() => setPanneSeverity("mineur")}
                className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-widest border ${
                  panneSeverity === "mineur"
                    ? "bg-amber-500 border-amber-500 text-white"
                    : "bg-white border-zinc-300 hover:border-amber-500"
                }`}
                data-testid="panne-severity-mineur"
              >
                ⚙ Panne mineure
              </button>
              <button
                type="button"
                onClick={() => setPanneSeverity("majeur")}
                className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-widest border ${
                  panneSeverity === "majeur"
                    ? "bg-red-600 border-red-600 text-white"
                    : "bg-white border-zinc-300 hover:border-red-600"
                }`}
                data-testid="panne-severity-majeur"
              >
                ⛔ Panne majeure
              </button>
            </div>
            <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">
              Mineure : réparation rapide possible · Majeure : véhicule immobilisé
            </div>
          </div>
          <div>
            <Label className="label-tag">Motif de la panne *</Label>
            <Textarea
              value={panneReason}
              onChange={(e) => setPanneReason(e.target.value)}
              placeholder="Embrayage HS, fuite de liquide, problème démarrage…"
              className="rounded-none border-zinc-900 min-h-[100px]"
              data-testid="panne-reason"
            />
          </div>
          <div>
            <Label className="label-tag">Date de la panne *</Label>
            <Input
              type="date"
              value={panneDate}
              onChange={(e) => setPanneDate(e.target.value)}
              className="rounded-none border-zinc-900 mono"
              data-testid="panne-date"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setPanneDialogOpen(false)}
              className="rounded-none border-zinc-900"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmPanne}
              className="rounded-none bg-amber-600 text-white hover:bg-amber-700"
              data-testid="confirm-panne"
            >
              Valider la panne
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add extra panne to a truck already broken */}
      <Dialog open={addPanneOpen} onOpenChange={setAddPanneOpen}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase tracking-tight inline-flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
              <Wrench size={22} weight="fill" className="text-amber-600" /> Ajouter une panne
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Nouvelle panne à empiler sur ce camion (il reste EN PANNE).
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="label-tag">Motif *</Label>
            <Textarea
              value={addPanneDesc}
              onChange={(e) => setAddPanneDesc(e.target.value)}
              placeholder="Ex : pneu avant droit crevé, fuite huile moteur…"
              className="rounded-none border-zinc-900 min-h-[80px]"
              data-testid="add-panne-desc"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-tag">Date</Label>
              <Input
                type="date"
                value={addPanneDate}
                onChange={(e) => setAddPanneDate(e.target.value)}
                className="rounded-none border-zinc-900 mono"
                data-testid="add-panne-date"
              />
            </div>
            <div>
              <Label className="label-tag">Sévérité</Label>
              <div className="flex gap-1 mt-1">
                <button
                  type="button"
                  onClick={() => setAddPanneSev("mineur")}
                  className={`flex-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest border ${
                    addPanneSev === "mineur"
                      ? "bg-amber-500 border-amber-500 text-white"
                      : "bg-white border-zinc-300"
                  }`}
                  data-testid="add-panne-sev-mineur"
                >
                  Mineure
                </button>
                <button
                  type="button"
                  onClick={() => setAddPanneSev("majeur")}
                  className={`flex-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest border ${
                    addPanneSev === "majeur"
                      ? "bg-red-600 border-red-600 text-white"
                      : "bg-white border-zinc-300"
                  }`}
                  data-testid="add-panne-sev-majeur"
                >
                  Majeure
                </button>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddPanneOpen(false)} className="rounded-none border-zinc-900">
              Annuler
            </Button>
            <Button onClick={confirmAddPanne} className="rounded-none bg-amber-600 text-white hover:bg-amber-700" data-testid="confirm-add-panne">
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resume from PANNE → ACTIF (multi-panne repair confirmation) */}
      <Dialog open={resumeActifOpen} onOpenChange={setResumeActifOpen}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase tracking-tight inline-flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
              <CheckCircle size={22} weight="fill" className="text-emerald-600" /> Remettre en actif
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Les réparations des pannes ci-dessous ont-elles été effectuées ? Vous pouvez remplir maintenant, ou valider sans info.
            </DialogDescription>
          </DialogHeader>
          {resumeActifItems.length === 0 ? (
            <div className="text-sm text-zinc-500 italic border-2 border-zinc-200 px-3 py-4 text-center">
              Aucun détail de panne à confirmer.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="resume-actif-list">
              {resumeActifItems.map((it, idx) => (
                <li
                  key={it.id}
                  className={`border-2 px-3 py-2 ${
                    it.repaired ? "border-emerald-500 bg-emerald-50" : "border-zinc-300 bg-white"
                  }`}
                  data-testid={`resume-actif-item-${it.id}`}
                >
                  <div className="flex items-start gap-2">
                    <span className="mono text-[10px] text-zinc-500 mt-1">#{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{it.description || "(sans motif)"}</div>
                      <div className="text-[11px] text-zinc-500">
                        {it.date ? new Date(it.date).toLocaleDateString("fr-FR") : "—"}
                        {it.severity === "majeur" ? " · MAJEURE" : " · mineure"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!it.repaired}
                        onChange={(e) => updateResumeItem(it.id, { repaired: e.target.checked })}
                        className="w-4 h-4 accent-emerald-600"
                        data-testid={`resume-repaired-${it.id}`}
                      />
                      <span className="font-bold uppercase tracking-widest text-[10px]">Réparé</span>
                    </label>
                    <div className="flex items-center gap-1 ml-auto">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Coût</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={it.repair_cost}
                        onChange={(e) => updateResumeItem(it.id, { repair_cost: e.target.value })}
                        className="rounded-none border-zinc-900 mono w-28 h-8"
                        placeholder="0.00"
                        data-testid={`resume-cost-${it.id}`}
                      />
                      <span className="text-xs text-zinc-500">€</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
            <Button
              variant="outline"
              onClick={() => setResumeActifOpen(false)}
              className="rounded-none border-zinc-900"
              data-testid="resume-cancel"
            >
              Annuler
            </Button>
            <Button
              variant="outline"
              onClick={confirmResumeWithoutInfo}
              className="rounded-none border-zinc-900"
              data-testid="resume-without-info"
            >
              Valider sans info
            </Button>
            <Button
              onClick={confirmResumeWithInfo}
              className="rounded-none bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="resume-with-info"
            >
              Valider et remettre en actif
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rendu date dialog */}
      <Dialog open={renduDialogOpen} onOpenChange={setRenduDialogOpen}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-md">
          <DialogHeader>
            <DialogTitle
              className="text-2xl font-black uppercase tracking-tight inline-flex items-center gap-2"
              style={{ fontFamily: "Chivo" }}
            >
              <Package size={22} weight="fill" className="text-blue-600" /> Camion rendu
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              À quelle date le véhicule a-t-il été rendu ?
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="label-tag">Date de rendu *</Label>
            <Input
              type="date"
              value={renduDate}
              onChange={(e) => setRenduDate(e.target.value)}
              className="rounded-none border-zinc-900 mono"
              data-testid="rendu-date"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRenduDialogOpen(false)}
              className="rounded-none border-zinc-900"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmRendu}
              className="rounded-none bg-blue-600 text-white hover:bg-blue-700"
              data-testid="confirm-rendu"
            >
              Valider le rendu
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RED TAG details dialog */}
      <Dialog open={redTagDialogOpen} onOpenChange={setRedTagDialogOpen}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-md">
          <DialogHeader>
            <DialogTitle
              className="text-2xl font-black uppercase tracking-tight inline-flex items-center gap-2"
              style={{ fontFamily: "Chivo" }}
            >
              <WarningOctagon size={22} weight="fill" className="text-red-600" /> Activer RED TAG
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Précisez les défauts qui rendent ce camion refusé par Amazon.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="label-tag">Détails du RED TAG *</Label>
            <Textarea
              value={redTagReason}
              onChange={(e) => setRedTagReason(e.target.value)}
              placeholder="Pare-choc avant déformé, jante endommagée, etc."
              className="rounded-none border-zinc-900 min-h-[100px]"
              data-testid="redtag-reason"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRedTagDialogOpen(false)}
              className="rounded-none border-zinc-900"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmRedTag}
              className="rounded-none bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-redtag"
            >
              Activer RED TAG
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Awaiting expert dialog */}
      <Dialog open={expertDialogOpen} onOpenChange={setExpertDialogOpen}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-md">
          <DialogHeader>
            <DialogTitle
              className="text-2xl font-black uppercase tracking-tight inline-flex items-center gap-2"
              style={{ fontFamily: "Chivo" }}
            >
              <Eyeglasses size={22} weight="fill" className="text-purple-600" /> Attente d&apos;expert
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Date prévue d&apos;arrivée de l&apos;expert sur ce véhicule.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="label-tag">Date d&apos;arrivée *</Label>
            <Input
              type="date"
              value={expertDate}
              onChange={(e) => setExpertDate(e.target.value)}
              className="rounded-none border-zinc-900 mono"
              data-testid="expert-date-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setExpertDialogOpen(false)}
              className="rounded-none border-zinc-900"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmExpertOn}
              className="rounded-none bg-purple-600 text-white hover:bg-purple-700"
              data-testid="confirm-expert"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PhotoLightbox
        photos={lightbox.list}
        index={lightbox.index}
        onClose={closeLightbox}
        onPrev={prevLightbox}
        onNext={nextLightbox}
      />

      {/* Quick RDV dialog */}
      <Dialog open={rdvDialogOpen} onOpenChange={setRdvDialogOpen}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-md w-[calc(100vw-1.5rem)] sm:w-auto max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle
              className="text-xl sm:text-2xl font-black uppercase tracking-tight inline-flex items-center gap-2"
              style={{ fontFamily: "Chivo" }}
            >
              <Calendar size={22} weight="bold" />
              Nouveau RDV pour {truck.license_plate}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="label-tag">Date *</Label>
                <Input
                  type="date"
                  value={rdvForm.date}
                  onChange={(e) => setRdvForm({ ...rdvForm, date: e.target.value })}
                  className="rounded-none border-zinc-900"
                  data-testid="quick-rdv-date"
                />
              </div>
              <div>
                <Label className="label-tag">Heure</Label>
                <Input
                  type="time"
                  value={rdvForm.time}
                  onChange={(e) => setRdvForm({ ...rdvForm, time: e.target.value })}
                  className="rounded-none border-zinc-900"
                />
              </div>
            </div>
            <div>
              <Label className="label-tag">Type</Label>
              <Select
                value={rdvForm.type}
                onValueChange={(v) => setRdvForm({ ...rdvForm, type: v })}
              >
                <SelectTrigger className="rounded-none border-zinc-900" data-testid="quick-rdv-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="expert">Expert</SelectItem>
                  <SelectItem value="clim">Recharge clim</SelectItem>
                  <SelectItem value="controle_technique">Contrôle technique</SelectItem>
                  <SelectItem value="revision">Révision</SelectItem>
                  <SelectItem value="mecanique">Mécanique</SelectItem>
                  <SelectItem value="carrosserie">Carrosserie</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Titre (optionnel)</Label>
              <Input
                value={rdvForm.title}
                onChange={(e) => setRdvForm({ ...rdvForm, title: e.target.value })}
                placeholder="Ex : Garage Renault Perpignan"
                className="rounded-none border-zinc-900"
              />
            </div>
            <div>
              <Label className="label-tag">Lieu (optionnel)</Label>
              <Input
                value={rdvForm.location}
                onChange={(e) => setRdvForm({ ...rdvForm, location: e.target.value })}
                className="rounded-none border-zinc-900"
              />
            </div>
            <div>
              <Label className="label-tag">Notes</Label>
              <Textarea
                value={rdvForm.notes}
                onChange={(e) => setRdvForm({ ...rdvForm, notes: e.target.value })}
                className="rounded-none border-zinc-900"
                rows={3}
              />
            </div>
            {/* ---- Finance / facture ---- */}
            <div className="border-t border-dashed border-zinc-300 pt-3">
              <Label className="label-tag block mb-2">Facture / Coût</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-tag">Montant (€)</Label>
                  <Input
                    data-testid="rdv-cost"
                    type="number"
                    step="0.01"
                    className="rounded-none border-zinc-900 mono"
                    value={rdvForm.cost}
                    onChange={(e) => setRdvForm({ ...rdvForm, cost: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="label-tag">Catégorie</Label>
                  <Select
                    value={rdvForm.category}
                    onValueChange={(v) => setRdvForm({ ...rdvForm, category: v })}
                  >
                    <SelectTrigger className="rounded-none border-zinc-900" data-testid="rdv-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="entretien">Entretien</SelectItem>
                      <SelectItem value="reparation">Réparation</SelectItem>
                      <SelectItem value="controle_technique">Contrôle technique</SelectItem>
                      <SelectItem value="pneus">Pneus</SelectItem>
                      <SelectItem value="carrosserie">Carrosserie</SelectItem>
                      <SelectItem value="assurance">Assurance</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="label-tag">Facture (PDF ou photo)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <label
                      className={`inline-flex items-center justify-center gap-2 border border-zinc-900 bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100 ${rdvUploading ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
                      data-testid="rdv-invoice-label"
                    >
                      {rdvUploading ? "Envoi…" : rdvForm.invoice_doc ? "Remplacer" : "+ Ajouter"}
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={uploadRdvInvoice}
                        disabled={rdvUploading}
                        className="hidden"
                        data-testid="rdv-invoice-input"
                      />
                    </label>
                    {rdvForm.invoice_doc && (
                      <>
                        <a
                          href={photoUrl(rdvForm.invoice_doc)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs mono underline text-zinc-700 hover:text-zinc-900"
                          data-testid="rdv-invoice-preview"
                        >
                          {String(rdvForm.invoice_doc).split("/").pop()}
                        </a>
                        <button
                          type="button"
                          onClick={() => setRdvForm((f) => ({ ...f, invoice_doc: null }))}
                          className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold border border-red-600 text-red-700 hover:bg-red-50"
                          data-testid="rdv-invoice-remove"
                        >
                          Retirer
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button
              onClick={() => setRdvDialogOpen(false)}
              className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-300 hover:border-zinc-900"
            >
              Annuler
            </button>
            <button
              onClick={submitRdv}
              className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700"
              data-testid="quick-rdv-submit"
            >
              Créer le RDV
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ImpactsByDriverPanel({ impacts, drivers = [] }) {
  const [open, setOpen] = React.useState(false);
  const byDriver = React.useMemo(() => {
    const map = new Map();
    for (const imp of impacts) {
      const name = (imp.driver || "").trim() || "— Inconnu —";
      if (!map.has(name)) {
        map.set(name, { name, count: 0, cost: 0, lastDate: "", severities: {} });
      }
      const e = map.get(name);
      e.count += 1;
      e.cost += Number(imp.cost || 0);
      if (imp.date && imp.date > e.lastDate) e.lastDate = imp.date;
      const sev = imp.severity || "mineur";
      e.severities[sev] = (e.severities[sev] || 0) + 1;
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count || b.cost - a.cost);
  }, [impacts]);

  const totalDrivers = byDriver.length;
  const totalCost = byDriver.reduce((s, e) => s + e.cost, 0);

  if (totalDrivers === 0) return null;

  return (
    <div
      className="mb-3 border border-zinc-300 bg-zinc-50"
      data-testid="impacts-by-driver-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-zinc-100"
        data-testid="impacts-by-driver-toggle"
      >
        <div className="flex items-center gap-2">
          <UsersThree size={16} weight="bold" className="text-zinc-700" />
          <span className="text-[11px] uppercase tracking-widest font-bold text-zinc-800">
            Chauffeurs ayant touché ce camion
          </span>
          <span className="px-1.5 py-0.5 text-[10px] font-bold bg-zinc-900 text-white mono">
            {totalDrivers}
          </span>
          {totalCost > 0 && (
            <span className="text-[10px] font-bold text-amber-700 mono ml-1">
              {totalCost.toFixed(0)} €
            </span>
          )}
        </div>
        <CaretRight
          size={12}
          weight="bold"
          className={`text-zinc-600 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-zinc-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100">
              <tr>
                <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-widest font-bold text-zinc-600">
                  Chauffeur
                </th>
                <th className="px-2 py-1.5 text-center text-[10px] uppercase tracking-widest font-bold text-zinc-600 w-16">
                  Impacts
                </th>
                <th className="px-2 py-1.5 text-center text-[10px] uppercase tracking-widest font-bold text-zinc-600">
                  Gravité
                </th>
                <th className="px-2 py-1.5 text-right text-[10px] uppercase tracking-widest font-bold text-zinc-600 w-24">
                  Coût total
                </th>
                <th className="px-2 py-1.5 text-right text-[10px] uppercase tracking-widest font-bold text-zinc-600 w-28">
                  Dernier
                </th>
              </tr>
            </thead>
            <tbody>
              {byDriver.map((e) => {
                const hasCritique = e.severities.critique > 0;
                const hasMajeur = e.severities.majeur > 0;
                const known = drivers.find((d) => d.name === e.name);
                return (
                  <tr
                    key={e.name}
                    className="border-t border-zinc-100 hover:bg-zinc-50"
                    data-testid={`driver-impact-row-${e.name}`}
                  >
                    <td className="px-3 py-1.5 font-bold text-zinc-800">
                      {e.name}
                      {!known && e.name !== "— Inconnu —" && (
                        <span
                          className="ml-1 text-[9px] uppercase tracking-widest text-zinc-400"
                          title="Chauffeur non enregistré dans la liste"
                        >
                          · non lié
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center mono font-bold">
                      {e.count}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <div className="inline-flex gap-1">
                        {hasCritique && (
                          <span className="px-1 py-0.5 text-[9px] font-bold bg-red-600 text-white">
                            {e.severities.critique} CRIT
                          </span>
                        )}
                        {hasMajeur && (
                          <span className="px-1 py-0.5 text-[9px] font-bold bg-orange-500 text-white">
                            {e.severities.majeur} MAJ
                          </span>
                        )}
                        {e.severities.mineur > 0 && (
                          <span className="px-1 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-800">
                            {e.severities.mineur} MIN
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right mono text-amber-700 font-bold">
                      {e.cost > 0 ? `${e.cost.toFixed(0)} €` : "—"}
                    </td>
                    <td className="px-2 py-1.5 text-right mono text-[10px] text-zinc-500">
                      {e.lastDate || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
